import React, { useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError, FILE_BASE } from "@/lib/api";
import { dmY, dmYhm } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { sanitizeUtr, isValidUtr, UTR_FORMAT_ERROR } from "@/lib/utr";
import { toastSlipUploaded, toastSlipApproved } from "@/components/AnimatedToasts";
import FireworksBurst from "@/components/FireworksBurst";
import { playApproveChime } from "@/lib/approveSound";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import SortHeader, { sortRows } from "@/components/SortHeader";
import AmountInput from "@/components/AmountInput";
import { UploadSimple, CheckCircle, XCircle, ArrowClockwise, Warning, Plus, Trash, MagnifyingGlassPlus, ArrowSquareOut, X, MagicWand, CircleNotch, PencilSimple, ShieldSlash } from "@phosphor-icons/react";
import useCanDelete from "@/lib/useCanDelete";
import { useAuth } from "@/context/AuthContext";
import PageShortcutHeader from "@/components/PageShortcutHeader";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

const UPLOAD_CONCURRENCY = 3;
const OCR_CONCURRENCY = 2;
// v1.25.27 — Bulk upload OCR: one primary attempt + one retry only on transient
// failure. NOT a routine "run OCR twice"; the retry only fires when the first
// call errors out (rate-limit, timeout, network burp). If both attempts fail
// the row is marked "Failed" and the admin can type the values or use the
// drawer's Re-scan Slip button later. Zero retries (v1.25.23) caused every
// transient Gemini hiccup to show up as "Failed" during bulk upload — this
// restores a reasonable safety net without the old 2-retry (3-attempt) waste.
const OCR_RETRIES = 1;
async function runWithConcurrency(items, limit, worker) {
    const executing = [];
    const results = [];
    for (const item of items) {
        const p = Promise.resolve().then(() => worker(item));
        results.push(p);
        const e = p.then(() => executing.splice(executing.indexOf(e), 1));
        executing.push(e);
        if (executing.length >= limit) await Promise.race(executing);
    }
    return Promise.all(results);
}

// v1.29.53 — Old-slip detection. Number of days a slip's Payment Date predates
// its Uploaded-At date; a slip is "old" when this exceeds 10 days.
const oldSlipDays = (u) => {
    if (!u || !u.payment_date || !u.uploaded_at) return 0;
    const pay = new Date(`${u.payment_date}T00:00:00`);
    const up = new Date(u.uploaded_at);
    if (isNaN(pay.getTime()) || isNaN(up.getTime())) return 0;
    const upMid = new Date(up.getFullYear(), up.getMonth(), up.getDate());
    const diff = Math.floor((upMid - pay) / 86400000);
    return diff > 0 ? diff : 0;
};
const isOldSlip = (u) => oldSlipDays(u) > 10;

export default function Uploads() {
    const canDelete = useCanDelete();
    const [oldSlipConfirm, setOldSlipConfirm] = useState(null); // v1.29.53 — { days, paymentDate, uploadedAt, onConfirm }
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [uploads, setUploads] = useState([]);
    // v1.29.83 — admin-only inline UTR correction (Transaction No → UTR).
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [utrEdit, setUtrEdit] = useState(null); // { id, value, saving }
    const [parties, setParties] = useState([]);
    const [tab, setTab] = useState(searchParams.get("status") || "pending");
    const [filterParty, setFilterParty] = useState(searchParams.get("party_id") || "all");
    const [includeInactive, setIncludeInactive] = useState(false);
    const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
    const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");
    // v1.27.78 — ?month=YYYY-MM shortcut used by the Dashboard Monthly Slip
    // Summary tile. Translates the full IST calendar month into date_from
    // and date_to inputs so the "View Details" link + the trend bars land
    // on a correctly-scoped Uploads list without extra clicks.
    useEffect(() => {
        const m = searchParams.get("month");
        if (!m || !/^\d{4}-\d{2}$/.test(m)) return;
        const [y, mm] = m.split("-").map((v) => parseInt(v, 10));
        const first = `${y}-${String(mm).padStart(2, "0")}-01`;
        const nextMonth = mm === 12 ? { y: y + 1, m: 1 } : { y, m: mm + 1 };
        const lastDay = new Date(nextMonth.y, nextMonth.m - 1, 0).getDate();
        const last = `${y}-${String(mm).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
        setDateFrom(first);
        setDateTo(last);
        setTab("all"); // show every status so counts match the tile
        // Strip the transient ?month param now that we've absorbed it.
        const p = new URLSearchParams(searchParams);
        p.delete("month");
        setSearchParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // v1.21.0 — Missing UTR filter (linked from Dashboard tile)
    const [missingUtrOnly, setMissingUtrOnly] = useState(searchParams.get("missing_utr") === "1");
    // v1.29.01 — UPI verification filter: 'all' | 'verified' | 'unverified'.
    // 'unverified' surfaces slips where the receiver UPI wasn't on the
    // company whitelist AND hasn't been manually cleared by an admin.
    const [upiFilter, setUpiFilter] = useState(searchParams.get("upi") || "all");
    // v1.31.43 — Portal filter: groups slips by the company portal their
    // Receiver UPI was paid to (derived from the Company UPI whitelist labels
    // + Portal master upi_ids). "__unknown__" = receiver UPI not whitelisted.
    const [portalFilter, setPortalFilter] = useState("all");
    const [portals, setPortals] = useState([]);
    const [companyUpis, setCompanyUpis] = useState([]);
    const upiPortalMap = React.useMemo(() => {
        const m = {};
        // Portal master is the authoritative UPI → portal grouping.
        portals.forEach((p) => (p.upi_ids || []).forEach((upi) => {
            const k = (upi || "").toLowerCase().trim();
            if (k && !m[k]) m[k] = (p.name || "").trim();
        }));
        // Fall back to the Company UPI "belongs to" label for UPIs not in any portal.
        companyUpis.forEach((u) => {
            const upi = (u.upi_id || "").toLowerCase().trim();
            const label = (u.label || "").trim();
            if (upi && label && !m[upi]) m[upi] = label;
        });
        return m;
    }, [companyUpis, portals]);
    // Case-insensitive de-duped portal names for the dropdown.
    const portalOptions = React.useMemo(() => {
        const seen = new Map(); // lowercased -> display name
        Object.values(upiPortalMap).forEach((name) => {
            const n = (name || "").trim();
            if (n && !seen.has(n.toLowerCase())) seen.set(n.toLowerCase(), n);
        });
        return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
    }, [upiPortalMap]);
    const portalOf = React.useCallback((u) => {
        const upi = (u.receiver_upi_id || "").toLowerCase().trim();
        return upi ? (upiPortalMap[upi] || "") : "";
    }, [upiPortalMap]);
    const [sort, setSort] = useState({ by: "uploaded_at", dir: "desc" });
    // v1.31.46 — remember each admin's last chosen sort column across visits.
    const sortLoadedForRef = React.useRef(null);
    useEffect(() => {
        const uid = user?.id || user?.email;
        if (!uid || sortLoadedForRef.current === uid) return;
        sortLoadedForRef.current = uid;
        try {
            const raw = localStorage.getItem(`micm.uploads.sort.${uid}`);
            if (raw) { const p = JSON.parse(raw); if (p && "by" in p && "dir" in p) setSort(p); }
        } catch { /* ignore bad storage */ }
    }, [user?.id, user?.email]);
    useEffect(() => {
        const uid = user?.id || user?.email;
        if (!uid) return;
        try { localStorage.setItem(`micm.uploads.sort.${uid}`, JSON.stringify(sort)); } catch { /* quota */ }
    }, [sort, user?.id, user?.email]);
    const sortedUploads = React.useMemo(() => {
        // Filter by UPI verification state first.
        let rows = uploads;
        if (upiFilter === "verified") {
            rows = rows.filter((u) => !u.receiver_upi_id || u.upi_matched || u.upi_manually_verified);
        } else if (upiFilter === "unverified") {
            rows = rows.filter((u) => u.receiver_upi_id && !u.upi_matched && !u.upi_manually_verified);
        }
        // v1.31.44 — Portal filter (case-insensitive; works alongside Party / Date / UPI filters).
        if (portalFilter !== "all") {
            const pf = portalFilter.toLowerCase();
            rows = rows.filter((u) => {
                const upi = (u.receiver_upi_id || "").toLowerCase().trim();
                if (portalFilter === "__unknown__") return !!upi && !upiPortalMap[upi];
                return !!upi && (upiPortalMap[upi] || "").toLowerCase() === pf;
            });
        }
        // v1.31.45 — decorate with derived portal + party-name so those columns sort as strings.
        const nameById = new Map(parties.map((p) => [p.id, p.name]));
        const decorated = rows.map((u) => ({
            ...u,
            _portal: portalOf(u),
            _party: nameById.get(u.party_id) || (u.party_id ? "(deleted party)" : ""),
        }));
        const sorted = sortRows(decorated, sort);
        // Bubble unverified rows to the top when the default sort (uploaded_at desc)
        // is active — makes them impossible to miss.
        if (upiFilter === "all" && sort.by === "uploaded_at" && sort.dir === "desc") {
            return [
                ...sorted.filter((u) => u.receiver_upi_id && !u.upi_matched && !u.upi_manually_verified),
                ...sorted.filter((u) => !(u.receiver_upi_id && !u.upi_matched && !u.upi_manually_verified)),
            ];
        }
        return sorted;
    }, [uploads, sort, upiFilter, portalFilter, upiPortalMap, portalOf, parties]);
    const [sel, setSel] = useState(null);
    const [form, setForm] = useState({ utr: "", amount: "", payment_date: "", notes: "", receiver_upi_id: "", portal_id: "" });
    useEffect(() => { api.get("/portals").then((r) => setPortals(r.data || [])).catch(() => {}); }, []);
    // OCR re-check state — populated by clicking "Run OCR" in the drawer.
    // Empty fields auto-fill; mismatched fields highlight red so admin sees
    // which typed value disagrees with what the model reads on the slip.
    const [ocrResult, setOcrResult] = useState(null);
    const [ocrBusy, setOcrBusy] = useState(false);

    useEffect(() => {
        // Whitelist of Company UPIs — used to power the receiver UPI dropdown
        // in the verify page (task: "let admin pick/select UPI if missing or 1st time")
        api.get("/settings/upis").then((r) => setCompanyUpis(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [orphanCount, setOrphanCount] = useState(0);

    // Keep URL in sync with filters (so links preserve context)
    // BUT preserve ?utr= / ?_open= / ?back= so the auto-open drawer effect (Task 3)
    // and the return-to-caller navigation still have access to them.
    useEffect(() => {
        const existing = new URLSearchParams(searchParams);
        const p = new URLSearchParams();
        if (tab && tab !== "pending") p.set("status", tab);
        if (filterParty !== "all") p.set("party_id", filterParty);
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        if (missingUtrOnly) p.set("missing_utr", "1");
        for (const key of ["utr", "_open", "back"]) {
            const v = existing.get(key);
            if (v) p.set(key, v);
        }
        setSearchParams(p, { replace: true });
    }, [tab, filterParty, dateFrom, dateTo, missingUtrOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async (silent = false) => {
        if (!silent) setRefreshing(true);
        try {
            const params = {};
            if (tab !== "all") params.status = tab;
            if (filterParty !== "all") params.party_id = filterParty;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (missingUtrOnly) params.missing_utr = true;
            // By default, hide slips of inactive parties. Admin can opt in via the toggle.
            if (includeInactive) params.include_inactive = true;
            const [r1, r2, r3] = await Promise.all([
                api.get("/uploads", { params }),
                api.get("/parties"),
                api.get("/uploads/orphans/count").catch(() => ({ data: { count: 0 } })),
            ]);
            setUploads(r1.data);
            setParties(r2.data);
            setOrphanCount(r3.data?.count || 0);
            setLastRefresh(new Date());
        } finally {
            if (!silent) setRefreshing(false);
        }
    }, [tab, filterParty, dateFrom, dateTo, includeInactive, missingUtrOnly]);

    useEffect(() => { load(); }, [load]);

    // Task 3: if URL has ?utr=..., auto-open the drawer for the first matching slip.
    // We track LAST-handled utr+timestamp combo. UtrSearch appends _open=<ts> so
    // re-clicking the same UTR after closing reliably re-opens the drawer.
    const utrHandledRef = React.useRef(null);
    useEffect(() => {
        const wantUtr = searchParams.get("utr");
        const wantId = searchParams.get("upload_id");
        const stamp = searchParams.get("_open") || "";
        const key = `${wantUtr || wantId || ""}|${stamp}`;
        if ((!wantUtr && !wantId) || sel || utrHandledRef.current === key) return;
        // v1.20.16 — open by upload_id directly (used when the ledger row has
        // no UTR yet — e.g. an entry the customer forgot to fill in)
        if (wantId && !wantUtr) {
            utrHandledRef.current = key;
            api.get(`/uploads/${wantId}`)
                .then(({ data }) => { if (data && data.id) openDrawer(data); })
                .catch((e) => toast.error(formatApiError(e)))
                .finally(() => {
                    const p = new URLSearchParams(searchParams);
                    p.delete("upload_id"); p.delete("_open");
                    setSearchParams(p, { replace: true });
                });
            return;
        }
        // Try the currently loaded list first
        const match = uploads.find((u) => (u.utr || "").toLowerCase() === wantUtr.toLowerCase());
        if (match) {
            utrHandledRef.current = key;
            openDrawer(match);
            const p = new URLSearchParams(searchParams);
            p.delete("utr");
            p.delete("_open");
            setSearchParams(p, { replace: true });
            return;
        }
        // Fallback: fetch directly (works even if current tab hides this slip)
        utrHandledRef.current = key;
        api.get(`/uploads/search`, { params: { q: wantUtr } })
            .then(({ data }) => {
                if (data && data.length > 0) openDrawer(data[0]);
                // v1.20.10 — silently ignore misses. The old error toast fired
                // spuriously after verifying a slip (URL still carried the UTR,
                // the slip had moved between tabs). Real 'search miss' cases
                // (typed a wrong UTR in the global search) are handled by the
                // search UI itself, not by this URL-param auto-open effect.
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => {
                const p = new URLSearchParams(searchParams);
                p.delete("utr");
                p.delete("_open");
                setSearchParams(p, { replace: true });
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploads, searchParams.get("utr"), searchParams.get("_open")]);

    // Auto-refresh every 20s + on window focus so newly uploaded slips appear without manual reload
    useEffect(() => {
        const interval = setInterval(() => load(true), 20000);
        const onFocus = () => load(true);
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
        };
    }, [load]);

    const partyName = useMemo(() => {
        const m = new Map(parties.map((p) => [p.id, p.name]));
        return (id) => m.get(id) || (id ? "(deleted party)" : "—");
    }, [parties]);

    const saveUtrEdit = async (u, force = false) => {
        const val = sanitizeUtr(utrEdit?.value || "");
        if (!val) { toast.error("UTR cannot be empty"); return; }
        if (!isValidUtr(val)) { toast.error(UTR_FORMAT_ERROR); return; }
        setUtrEdit((s) => s ? { ...s, saving: true } : s);
        try {
            await api.patch(`/uploads/${u.id}`, { utr: val, force_dup: force }, { skipAuthRedirect: true });
            toast.success("UTR updated");
            setUtrEdit(null);
            load();
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409 && !force) {
                setUtrEdit((s) => s ? { ...s, saving: false } : s);
                if (window.confirm("This UTR is already recorded.\n\nSave anyway? The slip will be tagged 'UTR Edited' for audit.")) {
                    saveUtrEdit(u, true);
                }
                return;
            }
            toast.error(formatApiError(e));
            setUtrEdit((s) => s ? { ...s, saving: false } : s);
        }
    };

    const openDrawer = (u) => {
        setSel(u);
        setForm({ utr: u.utr || "", amount: u.amount || "", payment_date: u.payment_date || "", notes: u.verification_notes || "", receiver_upi_id: u.receiver_upi_id || "", portal_id: u.portal_id || "" });
        setOcrResult(null); // fresh slip → clear any previous OCR overlay
        setReassignParty("");
    };

    const [dupWarning, setDupWarning] = useState(null); // { duplicates: [...], onConfirm }
    const [reassignParty, setReassignParty] = useState("");
    const [reassignBusy, setReassignBusy] = useState(false);
    const [rebuildBusy, setRebuildBusy] = useState(false);
    const [editBusy, setEditBusy] = useState(false);
    // Task 2 — bulk-approve pending slips
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [verifyBurst, setVerifyBurst] = useState(false); // v1.25.48 — confetti on approve
    const [bulkBusy, setBulkBusy] = useState(false);
    // v1.21.0 — Bulk-Trust OCR + Next Unchecked
    const [bulkTrustBusy, setBulkTrustBusy] = useState(false);
    const [nextInfo, setNextInfo] = useState(null); // { has_more, next, remaining }

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };
    const toggleSelectAllPending = (allIds) => {
        setSelectedIds((prev) => {
            const allSelected = allIds.length > 0 && allIds.every((id) => prev.has(id));
            return new Set(allSelected ? [] : allIds);
        });
    };
    const clearSelection = () => setSelectedIds(new Set());

    const runBulk = async (status) => {
        if (selectedIds.size === 0) return;
        const label = status === "verified" ? "approve" : "reject";
        if (!window.confirm(`${label === "approve" ? "Approve" : "Reject"} ${selectedIds.size} slip${selectedIds.size > 1 ? "s" : ""}?\n\nBackend guards (UPI whitelist, duplicate-UTR) still apply per slip. Any that fail those checks are listed at the end so you can review manually.`)) return;
        setBulkBusy(true);
        try {
            const ids = Array.from(selectedIds);
            const { data } = await api.post("/uploads/bulk-verify", { ids, status });
            const okN = (data.verified || []).length;
            const skN = (data.skipped || []).length;
            const failN = (data.failed || []).length;
            if (okN > 0) {
                if (label === "verify") {
                    toastSlipApproved(`${okN} slip${okN > 1 ? "s" : ""}`);
                    setVerifyBurst(true); // v1.25.48 — celebrate bulk approve
                    playApproveChime(); // v1.25.49 — audio cue
                } else {
                    toast.success(`${okN} slip${okN > 1 ? "s" : ""} ${label}d`);
                }
            }
            if (skN > 0) toast.warning(`${skN} skipped (UPI / duplicate-UTR guard) — open individually to override`);
            if (failN > 0) toast.error(`${failN} failed · check activity log`);
            clearSelection();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkBusy(false); }
    };

    // v1.21.0 — Bulk Trust OCR (selected)
    const runBulkTrust = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Mark ${selectedIds.size} slip${selectedIds.size > 1 ? "s" : ""} as OCR-trusted?\n\nThey'll be excluded from future bulk re-checks. The trust flag clears automatically if you later edit UTR, amount or date.`)) return;
        setBulkTrustBusy(true);
        try {
            const { data } = await api.post("/uploads/bulk-trust-ocr", { ids: Array.from(selectedIds) });
            toast.success(`${data.trusted} slip${data.trusted === 1 ? "" : "s"} marked OCR-trusted`);
            clearSelection();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkTrustBusy(false); }
    };

    // v1.21.0 — Trust every verified slip in one shot
    const trustAllVerified = async () => {
        if (!window.confirm("Mark EVERY verified slip as OCR-trusted?\n\nThis will exclude them from bulk OCR re-checks. Any slip's trust flag clears the moment you edit UTR/amount/date, so this is safe.")) return;
        setBulkTrustBusy(true);
        try {
            const { data } = await api.post("/uploads/bulk-trust-ocr", { all_verified: true });
            toast.success(`${data.trusted} verified slip${data.trusted === 1 ? "" : "s"} marked OCR-trusted`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkTrustBusy(false); }
    };

    // Track whether the verified-slip form fields have been edited vs. the loaded values
    // Run OCR against the stored slip image and auto-fill any empty fields;
    // keep the raw OCR result in `ocrResult` so we can red-highlight any
    // fields whose typed value disagrees with what the model reads.
    const runOcrRecheck = async (force = false) => {
        if (!sel) return;
        setOcrBusy(true);
        try {
            const { data } = await api.post(`/uploads/${sel.id}/ocr-recheck${force ? "?force=true" : ""}`);
            setOcrResult(data);
            // Auto-fill any currently-empty field
            setForm((f) => ({
                ...f,
                utr: f.utr || data.utr || "",
                amount: f.amount === "" || f.amount == null ? (data.amount != null ? String(data.amount) : f.amount) : f.amount,
                payment_date: f.payment_date || data.payment_date || "",
                receiver_upi_id: f.receiver_upi_id || data.receiver_upi_id || "",
            }));
            // Count mismatches so we can toast a summary
            const diffs = [];
            const cmpStr = (a, b) => String(a || "").trim() !== String(b || "").trim();
            if (data.utr && cmpStr(data.utr, sel.utr)) diffs.push("UTR");
            if (data.amount != null && Number(data.amount) !== Number(sel.amount || 0)) diffs.push("Amount");
            if (data.payment_date && data.payment_date !== sel.payment_date) diffs.push("Date");
            if (data.receiver_upi_id && cmpStr(data.receiver_upi_id, sel.receiver_upi_id)) diffs.push("Receiver UPI");
            if (data.cached) {
                // v1.25.23 — Cached hit — no Gemini call was made. Silent, just refresh UI.
                if (diffs.length > 0) toast.info(`Loaded saved OCR result · mismatches in: ${diffs.join(", ")} (highlighted red)`);
            } else if (diffs.length === 0) {
                if (data.ocr_verified) {
                    setSel((s) => s ? { ...s, ocr_verified: true, ocr_mismatch: false } : s);
                    toast.success("OCR verified — this slip won't be re-checked again");
                } else {
                    toast.success("OCR done — no mismatches found");
                }
            } else {
                toast.warning(`OCR done — mismatches in: ${diffs.join(", ")} (highlighted red)`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setOcrBusy(false);
        }
    };

    // Explicit "Trust this slip" action — for slips OCR consistently
    // misreads. Marks the slip ocr_verified without needing another OCR call.
    const trustSlip = async () => {
        if (!sel) return;
        if (!window.confirm("Mark this slip as OCR-trusted?\n\nIt will be excluded from future bulk OCR re-checks. The trust flag clears automatically if you later edit UTR, amount, or payment date.")) return;
        try {
            await api.post(`/uploads/${sel.id}/trust-ocr`);
            setSel((s) => s ? { ...s, ocr_verified: true, ocr_mismatch: false } : s);
            toast.success("Slip trusted — excluded from OCR re-checks");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Field-level mismatch helper. Returns true if OCR value exists AND
    // disagrees with the current form value. Empty form values are considered
    // "will be auto-filled", not "mismatched".
    const ocrMismatch = (key) => {
        if (!ocrResult) return false;
        const ocrVal = ocrResult[key];
        const curVal = form[key];
        if (ocrVal == null || ocrVal === "") return false;
        if (curVal == null || curVal === "") return false;
        if (key === "amount") return Number(ocrVal) !== Number(curVal);
        return String(ocrVal).trim() !== String(curVal).trim();
    };
    const mismatchCls = "border-rose-500 ring-2 ring-rose-200 bg-rose-50";

    // v1.25.23 — Show the saved OCR result the moment a PENDING slip drawer
    // opens. The backend returns the cached result if one exists (no Gemini
    // call), otherwise it runs OCR once and saves the result for next time.
    // A single re-scan happens only if the admin clicks the "Re-scan Slip"
    // toolbar button (force=true).
    useEffect(() => {
        if (!sel) return;
        if (sel.status !== "pending") return;
        if (ocrResult) return; // already loaded once for this slip
        runOcrRecheck(false);  // <-- honours backend cache
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sel?.id, sel?.status]);

    // "Use OCR values" — one-click adopt every OCR-suggested value into the
    // form. Admin then saves normally (bypasses the confusing
    // "Override & Verify Anyway" that used to save the wrong data).
    const useOcrValues = () => {
        if (!ocrResult) return;
        setForm((f) => ({
            ...f,
            utr: ocrResult.utr || f.utr,
            amount: ocrResult.amount != null ? String(ocrResult.amount) : f.amount,
            payment_date: ocrResult.payment_date || f.payment_date,
            receiver_upi_id: ocrResult.receiver_upi_id || f.receiver_upi_id,
        }));
        toast.success("OCR values applied — review & save");
    };

    const anyMismatch = ocrResult && (ocrMismatch("utr") || ocrMismatch("amount") || ocrMismatch("payment_date") || ocrMismatch("receiver_upi_id"));

    // v1.21.0 — Next unchecked navigation (pending drawer only)
    const loadNextInfo = useCallback(async () => {
        if (!sel || sel.status !== "pending") { setNextInfo(null); return; }
        try {
            const { data } = await api.get("/uploads/next-unchecked", { params: { after_id: sel.id } });
            setNextInfo(data);
        } catch (_e) { setNextInfo(null); }
    }, [sel]);
    useEffect(() => { loadNextInfo(); }, [loadNextInfo]);

    const gotoNextUnchecked = () => {
        if (!nextInfo?.has_more || !nextInfo.next) { toast.info("No more unchecked slips"); return; }
        const nx = nextInfo.next;
        if (nx.utr) {
            navigate(`/admin/uploads?utr=${encodeURIComponent(nx.utr)}&_open=${Date.now()}&status=pending`);
        } else {
            navigate(`/admin/uploads?upload_id=${nx.id}&_open=${Date.now()}&status=pending`);
        }
    };

    // Track whether the verified-slip form fields have been edited vs. the loaded values
    const isDirty = React.useMemo(() => {
        if (!sel) return false;
        const orig = {
            utr: sel.utr || "",
            amount: String(sel.amount ?? ""),
            payment_date: sel.payment_date || "",
            notes: sel.verification_notes || "",
            receiver_upi_id: sel.receiver_upi_id || "",
        };
        return (
            (form.utr || "") !== orig.utr ||
            String(form.amount ?? "") !== orig.amount ||
            (form.payment_date || "") !== orig.payment_date ||
            (form.notes || "") !== orig.notes ||
            (form.receiver_upi_id || "") !== orig.receiver_upi_id
        );
    }, [form, sel]);

    const [payError, setPayError] = useState("");

    // v1.29.85 — Pending-slip UTR/field save. The verified block already has a
    // Save button, but pending slips only had Verify — so editing a UTR to fix
    // an OCR mismatch showed no Save. This saves the correction, clears the
    // mismatch prompt (admin has reviewed) and flashes a green "UTR Updated".
    const [utrUpdatedFlash, setUtrUpdatedFlash] = useState(false);
    const applyPendingSaved = (payload) => {
        setSel((s) => s ? {
            ...s,
            utr: payload.utr,
            amount: payload.amount,
            payment_date: payload.payment_date,
            verification_notes: payload.notes,
            receiver_upi_id: payload.receiver_upi_id,
            ocr_mismatch: false,
            utr_edited: (payload.utr || "") !== (s.utr || "") ? true : s.utr_edited,
        } : s);
        setForm((f) => ({ ...f, notes: payload.notes ?? f.notes }));
        setOcrResult(null); // clear the mismatch prompt now that it's corrected + saved
        setUtrUpdatedFlash(true);
        setTimeout(() => setUtrUpdatedFlash(false), 2500);
        toast.success("UTR Updated");
        load();
    };
    const savePendingEdits = async (force = false) => {
        if (!sel || !isDirty) return;
        setPayError(""); setEditBusy(true);
        // v1.29.86 — when the admin overrides the OCR-read UTR with a manual
        // value, auto-stamp a remark preserving the ORIGINAL OCR values so the
        // manual correction is always auditable from the slip's notes.
        let notes = form.notes || "";
        const ocrUtr = (ocrResult?.utr || "").trim();
        const newUtr = (form.utr || "").trim();
        if (ocrUtr && newUtr && ocrUtr !== newUtr) {
            const stamp = new Date().toLocaleDateString("en-GB");
            const remark = `[Auto] UTR manually entered by admin (${stamp}). Original OCR read → UTR: ${ocrUtr}${ocrResult?.amount != null ? `, Amount: ₹${Number(ocrResult.amount).toLocaleString("en-IN")}` : ""}${ocrResult?.payment_date ? `, Date: ${dmY(ocrResult.payment_date)}` : ""}.`;
            if (!notes.includes(`Original OCR read → UTR: ${ocrUtr}`)) {
                notes = notes ? `${notes}\n${remark}` : remark;
            }
        }
        const payload = {
            utr: form.utr || "",
            amount: form.amount === "" ? null : Number(form.amount),
            payment_date: form.payment_date || "",
            notes,
            receiver_upi_id: form.receiver_upi_id || "",
            portal_id: form.portal_id || "",
            force_dup: force,
        };
        try {
            await api.patch(`/uploads/${sel.id}`, payload, { skipAuthRedirect: true });
            applyPendingSaved(payload);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409 && !force) {
                setEditBusy(false);
                if (window.confirm("This UTR is already recorded.\n\nSave anyway? The slip will be tagged 'UTR Edited' for audit.")) {
                    savePendingEdits(true);
                }
                return;
            }
            setPayError(formatApiError(e));
            toast.error(formatApiError(e));
        } finally {
            setEditBusy(false);
        }
    };

    const saveEdits = async () => {
        if (!sel || !isDirty) return;
        console.log("[payment-submit] update start", { uploadId: sel.id });
        setPayError("");
        setEditBusy(true);
        try {
            const payload = {
                utr: form.utr || "",
                amount: form.amount === "" ? null : Number(form.amount),
                payment_date: form.payment_date || "",
                notes: form.notes || "",
                receiver_upi_id: form.receiver_upi_id || "",
                portal_id: form.portal_id || "",
            };
            const wasFlagged = sel.ocr_mismatch === true;
            const { data } = await api.patch(`/uploads/${sel.id}`, payload, { skipAuthRedirect: true });
            console.log("[payment-submit] update success", { uploadId: sel.id });
            toast.success(sel.status === "verified" ? "Changes saved · ledger updated" : "Changes saved");
            if (sel.status === "verified") toastSlipApproved(fmt(sel.amount));
            load();
            // If we came from the ledger (?back=…), auto-close the drawer AND
            // navigate straight back to the caller — user shouldn't have to
            // hit ESC after saving.
            const live = new URLSearchParams(window.location.search);
            const back = live.get("back");
            if (back) {
                setSel(null);
                try { navigate(decodeURIComponent(back)); } catch { navigate(-1); }
                return;
            }
            // v1.20.14 — Task 3 (from earlier): auto-close the drawer once
            // the correction is saved. Also honour ?back so admin returns to
            // the ledger they came from (previously we stripped back but
            // didn't navigate → stranded on Uploads mid-reconcile).
            if (wasFlagged || sel.status === "verified") {
                const backParam = searchParams.get("back");
                const currentUtr = searchParams.get("utr") || "";
                const currentStamp = searchParams.get("_open") || "";
                if (currentUtr || currentStamp) {
                    utrHandledRef.current = `${currentUtr}|${currentStamp}`;
                }
                setSel(null);
                if (backParam) {
                    try { navigate(decodeURIComponent(backParam)); }
                    catch { navigate(-1); }
                    return;
                }
                if (currentUtr || currentStamp) {
                    const p = new URLSearchParams(searchParams);
                    p.delete("utr"); p.delete("_open");
                    setSearchParams(p, { replace: true });
                }
                return;
            }
            // Otherwise (still pending, wasn't flagged) — stay open with refreshed values
            setSel(data);
            setForm({
                utr: data.utr || "",
                amount: data.amount || "",
                payment_date: data.payment_date || "",
                notes: data.verification_notes || "",
                receiver_upi_id: data.receiver_upi_id || "",
            });
        } catch (e) {
            console.error("[payment-submit] update failed", e?.response?.status, e);
            setPayError(formatApiError(e));
            toast.error(formatApiError(e));
        } finally {
            setEditBusy(false);
        }
    };

    const reassign = async () => {
        if (!reassignParty || reassignParty === sel.party_id) return;
        const targetName = parties.find((p) => p.id === reassignParty)?.name || "the selected party";
        if (!window.confirm(
            `Reassign this verified slip to "${targetName}"?\n\nThe old party's ledger entries (payment + commission) will be reversed and re-posted to the new party using the new party's charge %.`
        )) return;
        setReassignBusy(true);
        try {
            const { data } = await api.patch(`/uploads/${sel.id}/party`, { party_id: reassignParty });
            toast.success(`Slip reassigned to ${targetName}`);
            setSel(data);
            setReassignParty("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReassignBusy(false);
        }
    };

    const rebuildLedger = async () => {
        if (!sel) return;
        if (!window.confirm(
            `Rebuild ledger entries for this slip?\n\nAny existing payment / commission / portal-fee rows linked to this slip will be wiped and re-posted on ${partyName(sel.party_id)} using the slip's current amount, UTR and date.\n\nUse this when a reassign or verify left the ledger out of sync.`
        )) return;
        setRebuildBusy(true);
        try {
            const { data } = await api.post(`/uploads/${sel.id}/rebuild-ledger`);
            toast.success(`Ledger rebuilt: cleared ${data.removed_txns}, created ${data.created_txns} entr${data.created_txns === 1 ? "y" : "ies"} on ${data.party_name || partyName(sel.party_id)}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRebuildBusy(false);
        }
    };

    const act = async (status, opts = {}) => {
        const { force_upi = false, force_dup = false, skipOldCheck = false } = opts;
        // v1.29.62 — Prefer the snapshot passed from the old-slip modal so the
        // approval survives the Sheet closing on outside-click (which nulls
        // `sel`). Normal verify/reject callers omit these → fall back to live state.
        const uploadId = opts.uploadId || sel?.id;
        const f = opts.formOverride || form;
        if (!uploadId) { toast.error("No slip selected to verify."); return; }
        // v1.29.53 — Old-slip approval guard: if the payment date is >10 days
        // before upload, confirm before approving. Rejection never warns.
        if (status === "verified" && !skipOldCheck && sel) {
            const days = oldSlipDays(sel);
            if (days > 10) {
                setOldSlipConfirm({
                    days,
                    paymentDate: sel.payment_date,
                    uploadedAt: sel.uploaded_at,
                    // v1.29.62 — snapshot the slip id + action so "Yes, Accept"
                    // approves using stored data (never a lost/stale closure).
                    uploadId: sel.id,
                    status,
                    opts,
                    formOverride: { ...form },
                });
                return;
            }
        }
        // v1.29.41 — crash guard: log the action, and on any failure show a
        // red inline error in the drawer — never let the global 401 handler
        // bounce the whole app to /login mid-verify.
        console.log("[payment-submit] verify start", { uploadId, status });
        setPayError("");
        try {
            const { data: verifyRes } = await api.post(`/uploads/${uploadId}/verify`, {
                status,
                notes: f.notes,
                utr: f.utr,
                amount: Number(f.amount) || undefined,
                payment_date: f.payment_date || undefined,
                force_upi,
                force_dup,
            }, { skipAuthRedirect: true });
            console.log("[payment-submit] verify success", { uploadId, status });
            toast.success(status === "verified" ? "Payment verified" : "Slip rejected");
            if (verifyRes?.portal_warning) toast.warning(verifyRes.portal_warning, { duration: 8000 });
            if (status === "verified") {
                setVerifyBurst(true); // v1.25.48 — celebrate single-slip verify
                playApproveChime(); // v1.25.49 — audio cue
            }
            // v1.20.14 — preserve return-to-source. If admin arrived here via a
            // ledger link (?back=/admin/reports?…&reconcile=1) they should be
            // taken RIGHT back to that reconcile view after verify — not left
            // stranded on the Payment Slips page. v1.20.10 was stripping the
            // back param without honouring it; now we navigate to it first.
            const backParam = searchParams.get("back");
            const currentUtr = searchParams.get("utr") || "";
            const currentStamp = searchParams.get("_open") || "";
            if (currentUtr || currentStamp) {
                utrHandledRef.current = `${currentUtr}|${currentStamp}`;
            }
            setSel(null);
            setDupWarning(null);
            if (backParam) {
                try { navigate(decodeURIComponent(backParam)); }
                catch { navigate(-1); }
                return;
            }
            // No back URL — just clean stale params so the auto-open effect
            // doesn't retry with a slip that has moved between tabs.
            if (currentUtr || currentStamp) {
                const p = new URLSearchParams(searchParams);
                p.delete("utr"); p.delete("_open");
                setSearchParams(p, { replace: true });
            }
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (
                e?.response?.status === 409 &&
                detail && typeof detail === "object" &&
                detail.code === "duplicate_utr" && !force_dup
            ) {
                setDupWarning({
                    duplicates: detail.duplicates || [],
                    message: detail.message || "This UTR is already used by another party.",
                    onConfirm: () => act(status, { force_upi, force_dup: true }),
                });
                return;
            }
            console.error("[payment-submit] verify failed", e?.response?.status, e);
            setPayError(formatApiError(e));
            toast.error(formatApiError(e));
        }
    };

    // v1.29.62 — Stable confirm handler for the old-slip warning modal. Reads
    // the slip id + action snapshot straight from modal state (not a stored
    // closure) so the id can never be lost between opening the modal and
    // clicking "Yes, Accept". Runs the SAME act("verified", …) approval path as
    // a normal slip; act() then closes the drawer, toasts, and reloads the list.
    const confirmOldSlipApprove = () => {
        const c = oldSlipConfirm;
        console.log("[old-slip] Yes,Accept clicked", { uploadId: c?.uploadId, status: c?.status });
        if (!c || !c.uploadId) {
            toast.error("Couldn't read the slip to approve — please reopen it and try again.");
            return;
        }
        setOldSlipConfirm(null);
        act(c.status || "verified", { ...(c.opts || {}), skipOldCheck: true, uploadId: c.uploadId, formOverride: c.formOverride });
    };

    const [addUpiModal, setAddUpiModal] = useState(null); // { upi, label } | null
    const [savingUpi, setSavingUpi] = useState(false);
    const [blockingUpi, setBlockingUpi] = useState(false);
    // v1.31.47 — On verify, if the receiver UPI isn't linked to any portal, prompt
    // to save it to a portal (adds to portal.upi_ids + Company UPI whitelist).
    const [portalLinkPrompt, setPortalLinkPrompt] = useState(null); // { upi, opts, portalId }
    const [linkingPortal, setLinkingPortal] = useState(false);

    const isUpiLinkedToPortal = (upiRaw) => {
        const upi = (upiRaw || "").toLowerCase().trim();
        if (!upi) return true; // no receiver UPI → nothing to link, skip prompt
        return portals.some((p) => (p.upi_ids || []).some((x) => (x || "").toLowerCase().trim() === upi));
    };

    const requestVerify = () => {
        const upi = (form.receiver_upi_id || "").trim();
        const opts = { force_upi: !sel?.upi_matched };
        if (upi && !isUpiLinkedToPortal(upi) && portals.length > 0) {
            setPortalLinkPrompt({ upi, opts, portalId: "" });
        } else {
            act("verified", opts);
        }
    };

    const saveUpiToPortalAndVerify = async () => {
        const { upi, opts, portalId } = portalLinkPrompt;
        if (!portalId) { toast.error("Select a portal first"); return; }
        setLinkingPortal(true);
        try {
            const portal = portals.find((p) => p.id === portalId);
            const nextUpis = Array.from(new Set([...(portal.upi_ids || []), upi]));
            await api.patch(`/portals/${portalId}`, { upi_ids: nextUpis });
            // Also add to the Company UPI whitelist so future slips auto-match (409 = already there).
            try { await api.post("/settings/upis", { upi_id: upi, label: portal.name, active: true }); } catch { /* already whitelisted */ }
            toast.success(`UPI linked to ${portal.name}`);
            api.get("/portals").then((r) => setPortals(r.data || [])).catch(() => {});
            setPortalLinkPrompt(null);
            act("verified", { ...opts, force_upi: true });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLinkingPortal(false); }
    };

    const skipPortalLinkAndVerify = () => {
        const opts = portalLinkPrompt?.opts || { force_upi: !sel?.upi_matched };
        setPortalLinkPrompt(null);
        act("verified", opts);
    };

    // v1.31.42 — Reject UPI: flag the receiver UPI as not belonging to us so any
    // future slip paid to it is blocked at the upload gate. Managed under
    // Administration → Blocked UPI List.
    const rejectReceiverUpi = async (upiRaw) => {
        const upi = (upiRaw || "").trim();
        if (!upi) return;
        const reason = window.prompt(`Block UPI "${upi}"?\n\nFuture payment slips paid to this UPI will be rejected at upload.\n\nReason (optional):`, "");
        if (reason === null) return; // cancelled
        setBlockingUpi(true);
        try {
            await api.post("/blocked-upis", { upi_id: upi, reason: (reason || "").trim() });
            toast.success("UPI rejected — added to Blocked UPI List");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBlockingUpi(false); }
    };

    const addUpiToWhitelist = async () => {
        // v1.31.39 — inline "Add This UPI" from the Slip Verification dialog.
        // Pre-fills the unregistered UPI and lets admin name the Portal/Company,
        // saves to Company UPI settings, then auto-matches the receiver UPI so
        // the admin can verify without leaving the page.
        const upi = (addUpiModal?.upi || sel?.receiver_upi_id || "").trim();
        if (!upi) return;
        setSavingUpi(true);
        try {
            await api.post("/settings/upis", { upi_id: upi, label: (addUpiModal?.label || "").trim() || "Added from slip verification", active: true });
            try {
                const { data: upis } = await api.get("/settings/upis");
                setCompanyUpis(Array.isArray(upis) ? upis : []);
            } catch { /* non-fatal */ }
            setSel((s) => s ? { ...s, upi_matched: true, receiver_upi_id: upi } : s);
            setForm((f) => ({ ...f, receiver_upi_id: upi }));
            setAddUpiModal(null);
            toast.success("UPI added successfully");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingUpi(false); }
    };    // Admin-side "Upload for Party" state — supports multiple files with per-file OCR
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [adminParty, setAdminParty] = useState("");
    const [slipItems, setSlipItems] = useState([]); // {id, file, ocring, amount, utr, payment_date, payment_app, receiver_upi_id, notes}

    const openAdminUpload = () => {
        setAdminParty("");
        setSlipItems([]);
        setUploadOpen(true);
    };

    // Task 4 — auto-open the "Upload for Party" dialog when navigating with ?upload=1
    // (used by the "Upload Party Slip" button on the admin Dashboard).
    const uploadFlagHandledRef = React.useRef(false);
    useEffect(() => {
        if (uploadFlagHandledRef.current) return;
        if (searchParams.get("upload") === "1") {
            uploadFlagHandledRef.current = true;
            openAdminUpload();
            const p = new URLSearchParams(searchParams);
            p.delete("upload");
            setSearchParams(p, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleAdminFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const items = files.map((f) => ({
            id: crypto.randomUUID(),
            file: f,
            preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
            ocring: f.type.startsWith("image/"),
            amount: "",
            utr: "",
            payment_date: new Date().toISOString().slice(0, 10),
            payment_app: "PhonePe",
            receiver_upi_id: "",
            notes: "",
            uploadStatus: null,
            uploadError: null,
        }));
        setSlipItems((prev) => [...prev, ...items]);

        // Cap OCR concurrency to 2 (Gemini rate-limits us at > ~3 parallel).
        // Retry on transient failures so admins don't have to type amount/date
        // manually just because Gemini burped once during a 20-slip batch.
        const runOcrOnce = async (item) => {
            if (!item.file.type.startsWith("image/")) return;
            let attempt = 0;
            while (attempt <= OCR_RETRIES) {
                try {
                    const fd = new FormData();
                    fd.append("file", item.file);
                    const { data } = await api.post("/uploads/ocr", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                        timeout: 60000,
                    });
                    setSlipItems((prev) => prev.map((s) => s.id === item.id ? {
                        ...s,
                        ocring: false,
                        amount: data.amount != null ? String(data.amount) : s.amount,
                        utr: data.utr || s.utr,
                        payment_date: data.payment_date || s.payment_date,
                        payment_app: data.payment_app || s.payment_app,
                        receiver_upi_id: data.receiver_upi_id || s.receiver_upi_id,
                    } : s));
                    if (data.note && /limit|unavailable/i.test(data.note)) toast.warning(data.note, { duration: 9000, id: "ocr-limit" });
                    return;
                } catch (err) {
                    attempt += 1;
                    if (attempt > OCR_RETRIES) {
                        // v1.25.27 — Surface the actual failure reason to the row
                        // so admins know whether it's a rate limit, timeout or bad
                        // image. Previously the row just said "Failed" with no
                        // context.
                        const msg = err?.response?.data?.detail || err?.message || "OCR failed";
                        setSlipItems((prev) => prev.map((s) => s.id === item.id ? {
                            ...s, ocring: false, ocrError: String(msg).slice(0, 140),
                        } : s));
                        return;
                    }
                    await new Promise((r) => setTimeout(r, 600 * attempt));
                }
            }
        };
        runWithConcurrency(items, OCR_CONCURRENCY, runOcrOnce);
    };

    const updateSlipItem = (id, patch) => setSlipItems((prev) => prev.map((s) => s.id === id ? { ...s, ...patch } : s));
    const removeSlipItem = (id) => setSlipItems((prev) => prev.filter((s) => s.id !== id));

    const submitAdminUpload = async (e) => {
        e.preventDefault();
        if (!adminParty) return toast.error("Select a party");
        if (slipItems.length === 0) return toast.error("Add at least one slip file");
        // Highlight invalid rows (missing amount / date / UTR) in red — no more opaque errors
        // v1.25.51 — UTR is now mandatory (matches the backend gate at /uploads).
        const missingReason = (s) => {
            const need = [];
            if (!s.amount) need.push("Amount");
            if (!s.payment_date) need.push("Date");
            if (!(s.utr || "").trim()) need.push("UTR");
            return need;
        };
        const invalidItems = slipItems.filter((s) => missingReason(s).length > 0);
        if (invalidItems.length > 0) {
            setSlipItems((prev) => prev.map((s) => {
                const need = missingReason(s);
                return need.length > 0
                    ? { ...s, uploadStatus: "error", uploadError: `${need.join(" & ")} missing` }
                    : s;
            }));
            toast.error(`${invalidItems.length} slip${invalidItems.length > 1 ? "s" : ""} missing UTR / Amount / Date — highlighted red. UTR is mandatory.`);
            return;
        }
        // Batch-level duplicate guard: reject if the same UTR appears twice in one submission
        const utrCounts = {};
        for (const s of slipItems) {
            const u = (s.utr || "").trim();
            if (u) utrCounts[u] = (utrCounts[u] || 0) + 1;
        }
        const dupUtrs = Object.entries(utrCounts).filter(([, c]) => c > 1).map(([u]) => u);
        if (dupUtrs.length > 0) {
            setSlipItems((prev) => prev.map((s) => dupUtrs.includes((s.utr || "").trim())
                ? { ...s, uploadStatus: "error", uploadError: `Duplicate UTR in this batch: ${(s.utr || "").trim()}` }
                : s));
            toast.error(`Duplicate UTR${dupUtrs.length > 1 ? "s" : ""} in batch: ${dupUtrs.slice(0, 3).join(", ")}. Remove or edit.`);
            return;
        }

        setUploadBusy(true);
        setSlipItems((prev) => prev.map((s) => ({ ...s, uploadStatus: "queued", uploadError: null })));
        let ok = 0, fail = 0;

        const uploadOne = async (s) => {
            setSlipItems((prev) => prev.map((x) => x.id === s.id ? { ...x, uploadStatus: "uploading" } : x));
            const fd = new FormData();
            fd.append("file", s.file);
            fd.append("party_id", adminParty);
            fd.append("amount", s.amount);
            fd.append("utr", s.utr);
            fd.append("payment_date", s.payment_date);
            fd.append("payment_app", s.payment_app);
            fd.append("receiver_upi_id", s.receiver_upi_id);
            fd.append("notes", s.notes);
            try {
                await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                ok += 1;
                setSlipItems((prev) => prev.map((x) => x.id === s.id ? { ...x, uploadStatus: "done" } : x));
            } catch (err) {
                fail += 1;
                const msg = formatApiError(err);
                toast.error(`Slip ${s.file.name}: ${msg}`);
                setSlipItems((prev) => prev.map((x) => x.id === s.id ? { ...x, uploadStatus: "error", uploadError: msg } : x));
            }
        };
        await runWithConcurrency(slipItems, UPLOAD_CONCURRENCY, uploadOne);

        setUploadBusy(false);
        if (ok > 0) toastSlipUploaded(ok, fail ? `${fail} failed — retry below.` : "Awaiting verification…");
        if (fail === 0) {
            // Small delay so admin sees green highlights
            setTimeout(() => {
                setUploadOpen(false);
                setSlipItems([]);
                setAdminParty("");
            }, 900);
        }
        load();
    };
    return (
        <div className="space-y-6" data-testid="uploads-page">
            {/* v1.29.53 — Old-slip approval warning modal (Payment Date >10 days
                before Upload Date). Shown for BOTH the queue row's drawer Accept
                and the detail-panel Accept; rejection is never gated. */}
            {oldSlipConfirm && createPortal(
                <div style={{ pointerEvents: "auto" }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" data-testid="old-slip-warning-modal" onClick={() => setOldSlipConfirm(null)}>
                    <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 text-amber-700">
                            <Warning size={22} weight="fill" />
                            <h3 className="font-display text-lg font-bold">Old payment slip</h3>
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-slate-700" data-testid="old-slip-warning-text">
                            This payment slip is <b>{oldSlipConfirm.days} days old</b> (Payment Date: {dmY(oldSlipConfirm.paymentDate)}, Uploaded: {dmY(oldSlipConfirm.uploadedAt)}). Please recheck the transaction before approving. Are you sure you want to accept this slip?
                        </p>
                        <div className="mt-5 flex justify-end gap-3">
                            <button type="button" data-testid="old-slip-cancel-btn" onClick={() => setOldSlipConfirm(null)} className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700">
                                Cancel — Recheck
                            </button>
                            <button type="button" data-testid="old-slip-accept-btn" onClick={confirmOldSlipApprove} className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700">
                                Yes, Accept
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            <FireworksBurst open={verifyBurst} onClose={() => setVerifyBurst(false)} />
            <div className="flex flex-wrap items-end justify-between gap-4">
                <PageShortcutHeader
                    action_id="page:payment-slips"
                    label="Payment Slips"
                    target_route="/admin/uploads"
                    eyebrow="Verification Queue"
                    title="Payment Slips"
                    titleClassName="mt-1 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900"
                />
                <div className="flex items-center gap-3">
                    <div className="hidden text-xs text-slate-500 sm:block">
                        Last refreshed {lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </div>
                    <button
                        data-testid="refresh-uploads-btn"
                        onClick={() => load(false)}
                        disabled={refreshing}
                        className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
                    >
                        <ArrowClockwise size={16} className={refreshing ? "animate-spin" : ""} />
                        <span>Refresh</span>
                    </button>
                    <ExportButton
                        endpoint={{ pdf: "/export/uploads.pdf", xlsx: "/export/uploads.xlsx" }}
                        params={{
                            ...(tab !== "all" ? { status: tab } : {}),
                            ...(filterParty !== "all" ? { party_id: filterParty } : {}),
                            ...(dateFrom ? { date_from: dateFrom } : {}),
                            ...(dateTo ? { date_to: dateTo } : {}),
                        }}
                        filenameBase="payment-slips"
                        testId="uploads-export"
                    />
                    <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="admin-upload-slip-btn" onClick={openAdminUpload} className="bg-blue-600 text-white hover:bg-blue-700">
                                <Plus size={16} className="mr-2" /> Upload for Party
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col p-0">
                            <DialogHeader className="border-b border-slate-200 px-6 py-4">
                                <DialogTitle className="font-display">Upload Slips on Behalf of a Party</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={submitAdminUpload} className="flex min-h-0 flex-1 flex-col">
                                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Party *</Label>
                                    <Select value={adminParty} onValueChange={setAdminParty}>
                                        <SelectTrigger data-testid="admin-upload-party" className="mt-1"><SelectValue placeholder="Select party" /></SelectTrigger>
                                        <SelectContent>
                                            {parties.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
                                        Slip files * (select multiple — OCR runs on each)
                                    </Label>
                                    <Input
                                        data-testid="admin-upload-file"
                                        type="file"
                                        accept="image/*,.pdf"
                                        multiple
                                        onChange={(e) => handleAdminFiles(e.target.files)}
                                        className="mt-1"
                                    />
                                </div>

                                {slipItems.length > 0 && (
                                    <div className="space-y-3" data-testid="admin-slips-list">
                                        {slipItems.map((s, idx) => {
                                            const status = s.uploadStatus;
                                            const cardClass =
                                                status === "done"
                                                    ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200"
                                                    : status === "error"
                                                        ? "border-rose-500 bg-rose-100 ring-2 ring-rose-300"
                                                        : status === "uploading"
                                                            ? "border-rose-400 bg-rose-50 ring-2 ring-rose-200 animate-pulse"
                                                            : status === "queued"
                                                                ? "border-rose-300 bg-rose-50/70"
                                                                : s.ocring
                                                                    ? "border-rose-300 bg-rose-50/60 ring-1 ring-rose-200 animate-pulse"
                                                                    : "border-slate-200 bg-slate-50";
                                            return (
                                            <div
                                                key={s.id}
                                                data-testid={`admin-slip-${idx}`}
                                                data-status={status || (s.ocring ? "ocr" : "ready")}
                                                className={`grid grid-cols-12 gap-2 rounded-md border p-3 transition-all duration-300 ${cardClass}`}
                                            >
                                                <div className="col-span-2">
                                                    {s.preview ? (
                                                        <img src={s.preview} alt="preview" className={`h-16 w-full rounded object-cover ${s.ocring ? "opacity-70" : ""}`} />
                                                    ) : (
                                                        <div className="flex h-16 w-full items-center justify-center rounded bg-slate-200 text-xs text-slate-500">PDF</div>
                                                    )}
                                                    <div className="mt-1 truncate text-[10px] text-slate-500">{s.file.name}</div>
                                                    {status === "done" ? (
                                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700" data-testid={`admin-slip-status-done-${idx}`}>
                                                            <CheckCircle size={10} weight="fill" /> Uploaded
                                                        </div>
                                                    ) : status === "error" ? (
                                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700" data-testid={`admin-slip-status-error-${idx}`}>
                                                            <XCircle size={10} weight="fill" /> Failed
                                                        </div>
                                                    ) : status === "uploading" ? (
                                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600" data-testid={`admin-slip-status-uploading-${idx}`}>
                                                            <CircleNotch size={10} weight="bold" className="animate-spin" /> Uploading…
                                                        </div>
                                                    ) : status === "queued" ? (
                                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500" data-testid={`admin-slip-status-queued-${idx}`}>
                                                            <CircleNotch size={10} weight="bold" /> Queued
                                                        </div>
                                                    ) : s.ocring ? (
                                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600" data-testid={`admin-slip-status-ocr-${idx}`}>
                                                            <MagicWand size={10} weight="fill" className="animate-pulse" /> Reading…
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="col-span-3">
                                                    <Label className="text-[10px] uppercase text-slate-500">Amount *</Label>
                                                    <AmountInput data-testid={`admin-slip-amount-${idx}`} variant="floating" value={s.amount} onChange={(e) => updateSlipItem(s.id, { amount: e.target.value })} className="mt-0.5 h-8 text-sm" wordsClassName="!text-[13px] !font-bold !text-gray-900" />
                                                </div>
                                                <div className="col-span-3">
                                                    <Label className="text-[10px] uppercase text-slate-500">UTR</Label>
                                                    <Input data-testid={`admin-slip-utr-${idx}`} value={s.utr} inputMode="numeric" maxLength={12} onChange={(e) => updateSlipItem(s.id, { utr: sanitizeUtr(e.target.value) })} className={`mt-0.5 h-8 font-mono text-xs ${s.utr && !isValidUtr(s.utr) ? "border-rose-300 bg-rose-50" : ""}`} />
                                                </div>
                                                <div className="col-span-3">
                                                    <Label className="text-[10px] uppercase text-slate-500">Date *</Label>
                                                    <Input data-testid={`admin-slip-date-${idx}`} type="date" value={s.payment_date} onChange={(e) => updateSlipItem(s.id, { payment_date: e.target.value })} className="mt-0.5 h-8 text-sm" />
                                                </div>
                                                <div className="col-span-1 flex items-end justify-end">
                                                    <button type="button" onClick={() => removeSlipItem(s.id)} className="rounded p-2 text-slate-400 hover:bg-white hover:text-rose-600">
                                                        <Trash size={14} />
                                                    </button>
                                                </div>
                                                <div className="col-span-6">
                                                    <Label className="text-[10px] uppercase text-slate-500">Receiver UPI</Label>
                                                    <Input data-testid={`admin-slip-upi-${idx}`} value={s.receiver_upi_id} onChange={(e) => updateSlipItem(s.id, { receiver_upi_id: e.target.value })} className="mt-0.5 h-8 font-mono text-xs" placeholder="mdimpex@ybl" />
                                                </div>
                                                <div className="col-span-6">
                                                    <Label className="text-[10px] uppercase text-slate-500">App</Label>
                                                    <Select value={s.payment_app} onValueChange={(v) => updateSlipItem(s.id, { payment_app: v })}>
                                                        <SelectTrigger data-testid={`admin-slip-app-${idx}`} className="mt-0.5 h-8 text-sm"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="PhonePe">PhonePe</SelectItem>
                                                            <SelectItem value="Google Pay">Google Pay</SelectItem>
                                                            <SelectItem value="Paytm">Paytm</SelectItem>
                                                            <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                                            <SelectItem value="Other">Other</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                {status === "error" && s.uploadError && (
                                                    <div className="col-span-12 rounded border border-rose-200 bg-rose-100/80 px-2 py-1 text-[11px] text-rose-800" data-testid={`admin-slip-error-msg-${idx}`}>
                                                        {s.uploadError}
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                                </div>
                                <div className="sticky bottom-0 flex flex-col gap-2 border-t border-slate-200 bg-transparent px-6 py-4">
                                    {uploadBusy && slipItems.length > 0 && (() => {
                                        const done = slipItems.filter((s) => s.uploadStatus === "done").length;
                                        const failed = slipItems.filter((s) => s.uploadStatus === "error").length;
                                        const total = slipItems.length;
                                        const pct = Math.round(((done + failed) / total) * 100);
                                        return (
                                            <div data-testid="admin-upload-progress-panel">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="font-medium text-slate-700">
                                                        Uploading {done + failed} of {total}
                                                        {failed > 0 && <span className="ml-1 text-rose-600">· {failed} failed</span>}
                                                    </span>
                                                    <span className="font-mono text-slate-500">{pct}%</span>
                                                </div>
                                                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                                                    <div
                                                        data-testid="admin-upload-progress-bar"
                                                        className="h-full bg-emerald-500 transition-all duration-300"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-xs text-slate-500" data-testid="admin-upload-count">
                                            {slipItems.length} slip{slipItems.length === 1 ? "" : "s"} ready
                                            {(() => {
                                                const ocrLeft = slipItems.filter((s) => s.ocring).length;
                                                return ocrLeft > 0 && !uploadBusy ? (
                                                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700" data-testid="admin-ocr-remaining-badge">
                                                        <MagicWand size={10} weight="fill" className="animate-pulse" />
                                                        Reading {ocrLeft}…
                                                    </span>
                                                ) : null;
                                            })()}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} disabled={uploadBusy}>Cancel</Button>
                                            <Button
                                                type="submit"
                                                data-testid="admin-upload-submit"
                                                disabled={uploadBusy || slipItems.length === 0 || slipItems.some((s) => s.ocring)}
                                                className="bg-blue-600 text-white hover:bg-blue-700"
                                            >
                                                <UploadSimple size={16} className="mr-2" />
                                                {uploadBusy
                                                    ? `Uploading ${slipItems.filter((s) => s.uploadStatus === "done" || s.uploadStatus === "error").length}/${slipItems.length}…`
                                                    : slipItems.some((s) => s.ocring)
                                                        ? "Reading slips…"
                                                        : `Upload ${slipItems.length || 0} slip${slipItems.length === 1 ? "" : "s"}`}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="text-base">
                    <TabsTrigger value="pending" data-testid="tab-pending" className="text-base">Pending</TabsTrigger>
                    <TabsTrigger value="verified" data-testid="tab-verified" className="text-base">Verified</TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="tab-rejected" className="text-base">Rejected</TabsTrigger>
                    <TabsTrigger value="all" data-testid="tab-all" className="text-base">All</TabsTrigger>
                </TabsList>

                {/* Orphan slips banner — surface deleted-party slips prominently */}
                {orphanCount > 0 && filterParty !== "unassigned" && (
                    <button
                        type="button"
                        data-testid="orphan-banner"
                        onClick={() => setFilterParty("unassigned")}
                        className="mt-4 flex w-full items-center justify-between gap-3 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 text-left shadow-sm transition hover:from-amber-100 hover:to-orange-100"
                    >
                        <div className="flex items-center gap-3">
                            <Warning size={20} weight="fill" className="shrink-0 text-amber-700" />
                            <div>
                                <div className="font-display text-sm font-semibold text-amber-900">
                                    {orphanCount} slip{orphanCount === 1 ? "" : "s"} without a valid party
                                </div>
                                <div className="text-xs text-amber-800/80">
                                    These belong to a deleted party. Click to view & reassign them to an active party.
                                </div>
                            </div>
                        </div>
                        <div className="shrink-0 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white">
                            Review {orphanCount}
                        </div>
                    </button>
                )}
                <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:grid-cols-3 lg:grid-cols-5">
                    <div>
                        <Label className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-600">Party</Label>
                        <Select value={filterParty} onValueChange={setFilterParty}>
                            <SelectTrigger data-testid="uploads-filter-party" className="mt-1 text-base"><SelectValue placeholder="All parties" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All parties</SelectItem>
                                <SelectItem value="unassigned" data-testid="filter-option-unassigned">
                                    Unassigned / deleted party{orphanCount > 0 ? ` (${orphanCount})` : ""}
                                </SelectItem>
                                {parties.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-600">Portal</Label>
                        <Select value={portalFilter} onValueChange={setPortalFilter}>
                            <SelectTrigger data-testid="uploads-filter-portal" className="mt-1 text-base"><SelectValue placeholder="All Portals" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Portals</SelectItem>
                                {portalOptions.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                                <SelectItem value="__unknown__" data-testid="filter-option-unknown-upi">Unknown UPI</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-600">From date</Label>
                        <Input data-testid="uploads-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 text-base" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-600">To date</Label>
                        <Input data-testid="uploads-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 text-base" />
                    </div>
                    <div className="flex items-end">
                        <button
                            data-testid="uploads-clear-dates"
                            onClick={() => { setDateFrom(""); setDateTo(""); setFilterParty("all"); setPortalFilter("all"); setIncludeInactive(false); }}
                            className="h-10 w-full rounded-md border border-slate-200 bg-white px-4 text-sm text-slate-700 hover:bg-slate-100"
                        >
                            Clear all
                        </button>
                    </div>
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs text-slate-600" title="Include slips of parties that were marked inactive">
                    <input
                        type="checkbox"
                        data-testid="include-inactive-toggle"
                        checked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)}
                        className="h-3.5 w-3.5 cursor-pointer rounded border-slate-300 accent-green-600"
                    />
                    <span>Include slips of <b>inactive parties</b> (hidden by default)</span>
                </label>
                {/* v1.29.01 — UPI verification filter chips */}
                <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="upi-filter-tabs">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">UPI:</span>
                    {[
                        { key: "all", label: "All" },
                        { key: "verified", label: "✓ Verified" },
                        { key: "unverified", label: "⚠ Unverified" },
                    ].map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            data-testid={`upi-filter-${f.key}`}
                            onClick={() => setUpiFilter(f.key)}
                            className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                                upiFilter === f.key
                                    ? (f.key === "unverified" ? "bg-rose-600 text-white shadow" : "bg-slate-800 text-white shadow")
                                    : "border border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                            }`}
                        >{f.label}</button>
                    ))}
                </div>
                {/* v1.21.0 — Missing UTR filter chip + Trust-all-verified shortcut */}
                {(missingUtrOnly || tab === "verified") && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {missingUtrOnly && (
                            <div className="inline-flex items-center gap-2 rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800" data-testid="missing-utr-chip">
                                <Warning size={12} weight="fill" /> Showing slips with missing UTR only
                                <button type="button" onClick={() => setMissingUtrOnly(false)} data-testid="missing-utr-clear" className="ml-1 rounded p-0.5 hover:bg-rose-200">
                                    <X size={11} weight="bold" />
                                </button>
                            </div>
                        )}
                        {tab === "verified" && (
                            <button
                                type="button"
                                onClick={trustAllVerified}
                                disabled={bulkTrustBusy}
                                data-testid="trust-all-verified-btn"
                                className="inline-flex items-center gap-1.5 rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                                title="Mark every verified slip OCR-trusted so bulk OCR re-check skips them"
                            >
                                <MagicWand size={11} weight="fill" /> {bulkTrustBusy ? "Working…" : "Trust ALL verified · OCR"}
                            </button>
                        )}
                    </div>
                )}
                <TabsContent value={tab} className="mt-6">
                    {uploads.length === 0 ? (
                        <EmptyState icon={UploadSimple} title={`No ${tab} slips`} description="Customers can upload payment slips from their portal." />
                    ) : (
                        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            {tab === "pending" && selectedIds.size > 0 && (
                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-200 bg-emerald-50 px-5 py-3" data-testid="bulk-action-bar">
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                                            {selectedIds.size} selected
                                        </span>
                                        <button
                                            type="button"
                                            onClick={clearSelection}
                                            className="text-[11px] font-medium text-slate-600 hover:text-slate-900 underline decoration-dotted"
                                        >Clear</button>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            data-testid="bulk-approve-btn"
                                            disabled={bulkBusy}
                                            onClick={() => runBulk("verified")}
                                            className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                                        >
                                            {bulkBusy ? "Working…" : `Approve ${selectedIds.size}`}
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="bulk-trust-btn"
                                            disabled={bulkTrustBusy}
                                            onClick={runBulkTrust}
                                            className="rounded-md border border-blue-300 bg-blue-50 px-4 py-1.5 text-sm font-semibold text-blue-800 shadow-sm hover:bg-blue-100 disabled:opacity-60"
                                            title="Mark selected slips as OCR-trusted (skips future re-checks)"
                                        >
                                            {bulkTrustBusy ? "…" : `Trust ${selectedIds.size} · OCR`}
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="bulk-reject-btn"
                                            disabled={bulkBusy}
                                            onClick={() => runBulk("rejected")}
                                            className="rounded-md border border-rose-300 bg-white px-4 py-1.5 text-sm font-semibold text-rose-700 shadow-sm hover:bg-rose-50 disabled:opacity-60"
                                        >Reject</button>
                                    </div>
                                </div>
                            )}
                            <div className="hidden overflow-x-auto md:block">
                                <table className="w-full text-base" data-testid="uploads-table">
                                    <thead>
                                        <tr className="text-left text-sm uppercase tracking-wider text-slate-700">
                                            {tab === "pending" && (
                                                <th className="px-5 py-3 w-8">
                                                    <input
                                                        type="checkbox"
                                                        data-testid="bulk-select-all"
                                                        checked={sortedUploads.length > 0 && sortedUploads.every((u) => selectedIds.has(u.id))}
                                                        onChange={() => toggleSelectAllPending(sortedUploads.map((u) => u.id))}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-green-600"
                                                        title="Select all on this page"
                                                    />
                                                </th>
                                            )}
                                            <th className="px-5 py-3 font-bold"><SortHeader field="uploaded_at" sort={sort} onChange={setSort} ascFirst>Uploaded</SortHeader></th>
                                            <th className="px-5 py-3 font-bold"><SortHeader field="_portal" sort={sort} onChange={setSort} ascFirst>Portal</SortHeader></th>
                                            <th className="px-5 py-3 font-bold"><SortHeader field="_party" sort={sort} onChange={setSort} ascFirst>Party</SortHeader></th>
                                            <th className="px-5 py-3 font-bold"><SortHeader field="utr" sort={sort} onChange={setSort} ascFirst>UTR</SortHeader></th>
                                            <th className="px-5 py-3 text-right font-bold"><SortHeader field="amount" align="right" sort={sort} onChange={setSort} ascFirst>Amount</SortHeader></th>
                                            <th className="px-5 py-3 font-bold"><SortHeader field="payment_date" sort={sort} onChange={setSort} ascFirst>Pay Date</SortHeader></th>
                                            <th className="px-5 py-3 font-bold"><SortHeader field="status" sort={sort} onChange={setSort} ascFirst>Status</SortHeader></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedUploads.map((u) => {
                                            const upiUnverified = u.receiver_upi_id && !u.upi_matched && !u.upi_manually_verified;
                                            return (
                                            <tr
                                                key={u.id}
                                                data-testid={`upload-row-${u.id}`}
                                                onClick={() => openDrawer(u)}
                                                className={`cursor-pointer border-t transition-colors ${
                                                    isOldSlip(u)
                                                        ? "border-pink-500 bg-pink-300 hover:bg-pink-400"
                                                        : upiUnverified
                                                        ? "border-blue-500 bg-blue-100 hover:bg-blue-200"
                                                        : u.ocr_mismatch
                                                            ? "border-rose-300 bg-rose-50 hover:bg-rose-100"
                                                            : u.utr_edited
                                                                ? "border-amber-400 bg-amber-100 hover:bg-amber-200"
                                                                : selectedIds.has(u.id)
                                                                    ? "border-green-300 bg-green-100 hover:bg-green-200"
                                                                    : "border-slate-100 hover:bg-slate-50"
                                                } ${u.utr_edited ? "border-l-4 border-l-amber-500" : ""}`}
                                                data-selected={selectedIds.has(u.id) ? "true" : "false"}
                                            >
                                                {tab === "pending" && (
                                                    <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            data-testid={`bulk-select-${u.id}`}
                                                            checked={selectedIds.has(u.id)}
                                                            onChange={() => toggleSelect(u.id)}
                                                            className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-green-600"
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-5 py-3 text-slate-700">
                                                    {(() => { const d = new Date(u.uploaded_at); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`; })()}
                                            {u.ocr_mismatch && (
                                                <div className="mt-1" data-testid={`ocr-mismatch-badge-${u.id}`} title={`OCR disagrees on: ${(u.ocr_mismatch_fields || []).join(", ")}`}>
                                                    <span className="inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-rose-800">
                                                        ⚠ OCR mismatch
                                                    </span>
                                                </div>
                                            )}
                                            {u.receiver_upi_id && (
                                                <div className="mt-1" data-testid={`upi-badge-${u.id}`}>
                                                    {upiUnverified ? (
                                                        <span className="inline-flex items-center gap-1 rounded bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white shadow-sm">
                                                            Unknown UPI
                                                        </span>
                                                    ) : u.upi_manually_verified ? (
                                                        <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800">
                                                            Manually Verified
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-800">
                                                            ✓ UPI Verified
                                                        </span>
                                                    )}
                                                    <span className="ml-1.5 font-mono text-[10px] text-slate-600">{u.receiver_upi_id}</span>
                                                </div>
                                            )}
                                                </td>
                                                <td className="px-5 py-3">
                                                    {portalOf(u) ? (
                                                        <span data-testid={`portal-cell-${u.id}`} className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
                                                            {portalOf(u)}
                                                        </span>
                                                    ) : <span className="text-slate-300">—</span>}
                                                </td>
                                                <td className="px-5 py-3">
                                                    <div className="font-medium text-slate-900">{partyName(u.party_id)}</div>
                                                    <div className="text-sm text-slate-500">
                                                        {u.uploaded_by_name && u.uploaded_by_role === "agent" ? (
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                                                Agent · {u.uploaded_by_name}
                                                            </span>
                                                        ) : (
                                                            u.uploaded_by
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-3 font-mono text-base font-bold text-slate-900" onClick={(e) => { if (utrEdit?.id === u.id) e.stopPropagation(); }}>
                                                    {utrEdit?.id === u.id ? (
                                                        <div className="flex flex-col gap-1.5" data-testid={`utr-edit-box-${u.id}`} onClick={(e) => e.stopPropagation()}>
                                                            <div className="text-[10px] font-sans font-normal uppercase tracking-wider text-slate-500">
                                                                Current: <span className="font-mono font-semibold text-slate-700">{u.utr || "—"}</span>
                                                                {u.reference_number && <span className="ml-2">· Orig Ref: <span className="font-mono font-semibold text-amber-700">{u.reference_number}</span></span>}
                                                            </div>
                                                            <Input
                                                                autoFocus
                                                                data-testid={`utr-edit-input-${u.id}`}
                                                                value={utrEdit.value}
                                                                inputMode="numeric"
                                                                maxLength={12}
                                                                onChange={(e) => setUtrEdit((s) => ({ ...s, value: sanitizeUtr(e.target.value) }))}
                                                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveUtrEdit(u); } if (e.key === "Escape") setUtrEdit(null); }}
                                                                className="h-8 w-44 font-mono text-sm"
                                                            />
                                                            <div className="flex items-center gap-1.5">
                                                                <button type="button" data-testid={`utr-edit-save-${u.id}`} disabled={utrEdit.saving}
                                                                    onClick={() => saveUtrEdit(u)}
                                                                    className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                                                                    {utrEdit.saving ? "Saving…" : "Save"}
                                                                </button>
                                                                <button type="button" data-testid={`utr-edit-cancel-${u.id}`} disabled={utrEdit.saving}
                                                                    onClick={() => setUtrEdit(null)}
                                                                    className="rounded border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60">
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <span className={u.utr_edited ? "text-amber-700 font-bold" : ""}>{u.utr || "—"}</span>
                                                            {isAdmin && (
                                                                <button type="button" title="Correct UTR / Transaction number"
                                                                    data-testid={`utr-edit-btn-${u.id}`}
                                                                    onClick={(e) => { e.stopPropagation(); setUtrEdit({ id: u.id, value: u.utr || "", saving: false }); }}
                                                                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                                                                    <PencilSimple size={14} />
                                                                </button>
                                                            )}
                                                            {u.utr_edited && (
                                                                <span data-testid={`utr-edited-tag-${u.id}`} className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-sans font-bold uppercase tracking-wider text-amber-800">
                                                                    UTR Edited
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-right tabular-nums font-bold text-slate-900">{fmt(u.amount)}</td>
                                                <td className="px-5 py-3 text-slate-700">
                                                    {dmY(u.payment_date)}
                                                    {isOldSlip(u) && (
                                                        <div className="mt-1">
                                                            <span data-testid={`old-slip-badge-${u.id}`} className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800">
                                                                <Warning size={10} weight="fill" /> Old slip — {oldSlipDays(u)} days old
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                    {/* v1.25.80 — Day / filter total row. Sums every visible row's
                                        amount so admins running "date=today, all parties" get the
                                        one number they actually came for at the bottom of the table. */}
                                    {sortedUploads.length > 0 && (
                                        <tfoot data-testid="uploads-total-row">
                                            <tr className="border-t-2 border-slate-300 bg-slate-50 text-base font-bold text-slate-900">
                                                {tab === "pending" && <td className="px-5 py-3" />}
                                                <td className="px-5 py-3 text-sm uppercase tracking-wider text-slate-700">Total ({sortedUploads.length} slip{sortedUploads.length === 1 ? "" : "s"})</td>
                                                <td className="px-5 py-3" />
                                                <td className="px-5 py-3" />
                                                <td className="px-5 py-3" />
                                                <td className="px-5 py-3 text-right tabular-nums text-emerald-800 text-lg" data-testid="uploads-total-amount">
                                                    {fmt(sortedUploads.reduce((s, u) => s + Number(u.amount || 0), 0))}
                                                </td>
                                                <td className="px-5 py-3" />
                                                <td className="px-5 py-3" />
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                            {/* v1.29.95 — Mobile card list: no left/right scrolling to read a
                                pending slip. Desktop keeps the full table above. Tap a card to
                                open the full verification drawer. */}
                            <div className="space-y-2 p-2 md:hidden" data-testid="uploads-cards">
                                {sortedUploads.length === 0 && (
                                    <div className="py-8 text-center text-sm text-slate-400">No slips</div>
                                )}
                                {sortedUploads.map((u) => {
                                    const upiUnverifiedC = u.receiver_upi_id && !u.upi_matched && !u.upi_manually_verified;
                                    return (
                                        <div
                                            key={u.id}
                                            data-testid={`upload-card-${u.id}`}
                                            onClick={() => openDrawer(u)}
                                            className={`rounded-lg border p-3 transition-colors ${
                                                isOldSlip(u)
                                                    ? "border-pink-400 bg-pink-100"
                                                    : upiUnverifiedC
                                                    ? "border-blue-400 bg-blue-50"
                                                    : u.ocr_mismatch
                                                    ? "border-rose-300 bg-rose-50"
                                                    : u.utr_edited
                                                    ? "border-l-4 border-amber-400 bg-amber-50"
                                                    : "border-slate-200 bg-white"
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex min-w-0 items-start gap-2">
                                                    {tab === "pending" && (
                                                        <input
                                                            type="checkbox"
                                                            data-testid={`bulk-select-card-${u.id}`}
                                                            checked={selectedIds.has(u.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={() => toggleSelect(u.id)}
                                                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-green-600"
                                                        />
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="flex flex-wrap items-center gap-1.5">
                                                            <div className="truncate font-semibold text-slate-900">{partyName(u.party_id)}</div>
                                                            {portalOf(u) && (
                                                                <span data-testid={`portal-chip-card-${u.id}`} className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-indigo-700">
                                                                    {portalOf(u)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-0.5 break-all font-mono text-xs text-slate-600">UTR {u.utr || "—"}</div>
                                                        {u.uploaded_by_name && u.uploaded_by_role === "agent" && (
                                                            <div className="mt-0.5 text-[10px] font-semibold text-indigo-700">Agent · {u.uploaded_by_name}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="shrink-0 text-right">
                                                    <div className="font-bold tabular-nums text-slate-900">{fmt(u.amount)}</div>
                                                    <div className="mt-0.5"><StatusBadge status={u.status} /></div>
                                                </div>
                                            </div>
                                            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
                                                <span className="text-slate-500">{dmY(u.payment_date)}</span>
                                                {u.ocr_mismatch && <span className="rounded bg-rose-100 px-1.5 py-0.5 font-bold uppercase text-rose-800">⚠ OCR mismatch</span>}
                                                {upiUnverifiedC && <span className="rounded bg-blue-600 px-1.5 py-0.5 font-bold uppercase text-white">Unknown UPI</span>}
                                                {u.utr_edited && <span className="rounded bg-amber-100 px-1.5 py-0.5 font-bold uppercase text-amber-800">UTR Edited</span>}
                                                {isOldSlip(u) && <span className="rounded bg-amber-100 px-1.5 py-0.5 font-bold uppercase text-amber-800">Old slip</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                                {sortedUploads.length > 0 && (
                                    <div className="flex items-center justify-between rounded-lg border-2 border-slate-300 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-900" data-testid="uploads-cards-total">
                                        <span>Total ({sortedUploads.length} slip{sortedUploads.length === 1 ? "" : "s"})</span>
                                        <span className="tabular-nums text-emerald-800">{fmt(sortedUploads.reduce((s, u) => s + Number(u.amount || 0), 0))}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <Sheet open={!!sel} onOpenChange={(o) => {
                if (!o) {
                    setSel(null);
                    // Always read the LIVE URL — closure-captured searchParams may be stale
                    // by the time the Sheet fires onOpenChange (Escape / X click).
                    const live = new URLSearchParams(window.location.search);
                    const back = live.get("back");
                    if (back) {
                        try {
                            navigate(decodeURIComponent(back));
                        } catch {
                            navigate(-1);
                        }
                    }
                }
            }}>
                <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-5xl">
                    {sel && (
                        <div className="grid h-full grid-cols-1 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
                            {/* LEFT — slip preview (compact, zoomable) */}
                            <div className="relative flex flex-col bg-slate-900 lg:sticky lg:top-0 lg:h-screen">
                                <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Payment slip</div>
                                        <div className="mt-0.5 text-xs font-semibold text-white truncate max-w-[9rem]">{partyName(sel.party_id)}</div>
                                    </div>
                                    <StatusBadge status={sel.status} />
                                </div>
                                <SlipPreview fileUrl={`${FILE_BASE}${sel.file_url}`} />
                            </div>

                            {/* RIGHT — details & actions */}
                            <div className="flex flex-col px-5 py-4">
                                <SheetHeader>
                                    <SheetTitle className="font-display text-xl">Slip Verification</SheetTitle>
                                </SheetHeader>
                                <div className="mt-3 space-y-3">
                                    <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-xs">
                                        <Meta label="Party" value={partyName(sel.party_id)} />
                                        <Meta label="Uploaded by" value={sel.uploaded_by} />
                                        <Meta label="Uploaded at" value={dmYhm(sel.uploaded_at)} />
                                        <Meta label="Payment App" value={sel.payment_app || "—"} />
                                        <Meta label="Receiver UPI" value={sel.receiver_upi_id || "—"} />
                                        <Meta label="Current status" value={<StatusBadge status={sel.status} />} />
                                        {sel.verified_at && (
                                            <Meta
                                                label={sel.status === "rejected" ? "Rejected on" : "Approved on"}
                                                value={<span data-testid="verified-at-meta">{dmYhm(sel.verified_at)}{sel.verified_by ? ` · ${sel.verified_by}` : ""}</span>}
                                            />
                                        )}
                                        {sel.notes && <Meta label="Customer note" value={sel.notes} />}
                                    </div>

                                    {Array.isArray(sel.ocr_edit_log) && sel.ocr_edit_log.length > 0 && (
                                        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="ocr-edit-audit">
                                            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-800">OCR Edit History</div>
                                            <div className="mt-2 space-y-1.5">
                                                {sel.ocr_edit_log.map((e, i) => (
                                                    <div key={i} data-testid={`ocr-edit-log-${i}`} className="rounded border border-amber-200 bg-white px-2 py-1.5 text-[11px] text-slate-700">
                                                        <span className="font-semibold uppercase text-slate-800">{e.field}</span>:
                                                        <span className="ml-1 text-slate-500">OCR</span> <span className="font-mono">{String(e.ocr_original ?? "—") || "—"}</span>
                                                        <span className="mx-1 text-slate-400">→</span>
                                                        <span className="font-mono font-semibold text-amber-900">{String(e.new ?? "—") || "—"}</span>
                                                        <span className="ml-1 text-slate-400">· {e.by} · {dmYhm(e.at)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {sel.status === "pending" && !sel.upi_matched && (
                                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="upi-mismatch-banner">
                                            <div className="flex items-start gap-3">
                                                <Warning size={22} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                                                <div className="flex-1">
                                                    <div className="font-display text-sm font-semibold text-amber-900">
                                                        Receiver UPI not in your whitelist
                                                    </div>
                                                    <div className="mt-1 text-sm text-amber-800">
                                                        {sel.receiver_upi_id ? (
                                                            <>Slip shows <span className="font-mono font-bold">{sel.receiver_upi_id}</span> — not in your Company UPI list. Verify only if you're sure the payment reached your account.</>
                                                        ) : (
                                                            <>No receiver UPI detected on this slip. Cannot confirm the payment reached your account.</>
                                                        )}
                                                    </div>
                                                    {sel.receiver_upi_id && (
                                                        <button
                                                            type="button"
                                                            data-testid="add-upi-quick-btn"
                                                            onClick={() => setAddUpiModal({ upi: sel.receiver_upi_id, label: "" })}
                                                            className="mt-3 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-amber-800"
                                                            title="Add this UPI to Company UPI settings without leaving this page."
                                                        >
                                                            + Add This UPI
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {addUpiModal && (
                                        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" data-testid="add-upi-modal" onClick={() => setAddUpiModal(null)}>
                                            <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                                                <div className="font-display text-base font-bold text-slate-900">Add Receiver UPI</div>
                                                <p className="mt-1 text-xs text-slate-500">Saves to Company UPI settings without leaving this page.</p>
                                                <div className="mt-4 space-y-3">
                                                    <div>
                                                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">UPI ID</label>
                                                        <input data-testid="add-upi-id-input" value={addUpiModal.upi} readOnly className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-slate-100 px-3 font-mono text-sm text-slate-700" />
                                                    </div>
                                                    <div>
                                                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Portal / Company name</label>
                                                        {(() => {
                                                            // v1.31.40 — merged dropdown of existing portals: Company UPI labels + Portal master list.
                                                            const names = Array.from(new Set([
                                                                ...companyUpis.map((u) => (u.label || "").trim()),
                                                                ...portals.map((p) => (p.name || "").trim()),
                                                            ].filter(Boolean))).sort((a, b) => a.localeCompare(b));
                                                            return (<>
                                                                <select
                                                                    data-testid="add-upi-portal-select"
                                                                    value={addUpiModal.addingNew ? "__new__" : (addUpiModal.label || "")}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        if (v === "__new__") setAddUpiModal((m) => ({ ...m, addingNew: true, label: "" }));
                                                                        else setAddUpiModal((m) => ({ ...m, addingNew: false, label: v }));
                                                                    }}
                                                                    className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none"
                                                                >
                                                                    <option value="">Select portal / company…</option>
                                                                    {names.map((n) => <option key={n} value={n}>{n}</option>)}
                                                                    <option value="__new__">+ Add New Portal</option>
                                                                </select>
                                                                {addUpiModal.addingNew && (
                                                                    <input data-testid="add-upi-label-input" value={addUpiModal.label} onChange={(e) => setAddUpiModal((m) => ({ ...m, label: e.target.value }))} placeholder="e.g. SwastikPay" autoFocus className="mt-2 h-9 w-full rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
                                                                )}
                                                            </>);
                                                        })()}
                                                    </div>
                                                </div>
                                                <div className="mt-5 flex justify-end gap-2">
                                                    <button type="button" onClick={() => setAddUpiModal(null)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                                                    <button type="button" data-testid="add-upi-save-btn" onClick={addUpiToWhitelist} disabled={savingUpi} className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white hover:bg-emerald-700 disabled:opacity-50">{savingUpi ? "Saving…" : "Save UPI"}</button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Verification Fields</div>
                                                {sel.ocr_verified && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800" data-testid="ocr-trusted-chip" title="OCR agrees with stored values. Bulk re-checks will skip this slip until you edit a compared field.">
                                                        <CheckCircle size={10} weight="fill" /> OCR trusted
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {sel.status === "verified" && !sel.ocr_verified && (
                                                    <button
                                                        type="button"
                                                        data-testid="drawer-trust-slip"
                                                        onClick={trustSlip}
                                                        className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                                                        title="Mark this slip as OCR-trusted — bulk re-checks will skip it. Use this when OCR keeps misreading a slip that's actually correct."
                                                    >
                                                        <CheckCircle size={12} weight="fill" /> Trust this slip
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    data-testid="drawer-ocr-recheck"
                                                    onClick={() => runOcrRecheck(true)}
                                                    disabled={ocrBusy}
                                                    className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                                                    title="Re-scan this slip with Gemini OCR (bypasses the saved result). Uses one Gemini call. Values that disagree with what you typed will be highlighted red."
                                                >
                                                    <MagicWand size={12} weight="fill" className={ocrBusy ? "animate-pulse" : ""} />
                                                    {ocrBusy ? "Scanning…" : "Re-scan Slip"}
                                                </button>
                                                {anyMismatch && (
                                                    <button
                                                        type="button"
                                                        data-testid="drawer-use-ocr-values"
                                                        onClick={useOcrValues}
                                                        className="inline-flex items-center gap-1.5 rounded-md border border-emerald-400 bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
                                                        title="Copy OCR values into the fields — you can then edit any of them before saving"
                                                    >
                                                        ✓ Use OCR values
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {ocrResult && (
                                            <div className="rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2 text-[11px] text-blue-900" data-testid="ocr-summary">
                                                <span className="font-semibold">OCR read:</span>{" "}
                                                {ocrResult.utr && <span className="mr-2">UTR <span className="font-mono">{ocrResult.utr}</span></span>}
                                                {ocrResult.amount != null && <span className="mr-2">Amount <span className="font-mono">₹{Number(ocrResult.amount).toLocaleString("en-IN")}</span></span>}
                                                {ocrResult.payment_date && <span className="mr-2">Date <span className="font-mono">{dmY(ocrResult.payment_date)}</span></span>}
                                                {ocrResult.receiver_upi_id && <span className="mr-2">UPI <span className="font-mono">{ocrResult.receiver_upi_id}</span></span>}
                                                <span className="ml-1 italic text-blue-700">· mismatches in red</span>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <Label className="text-xs">UTR {ocrMismatch("utr") && <span className="ml-1 text-rose-600 font-bold" data-testid="mismatch-utr">⚠ OCR: {ocrResult.utr}</span>}</Label>
                                                <Input data-testid="verify-utr" value={form.utr} inputMode="numeric" maxLength={12} onChange={(e) => setForm({ ...form, utr: sanitizeUtr(e.target.value) })} className={`mt-1 ${ocrMismatch("utr") ? mismatchCls : ""} ${form.utr && !isValidUtr(form.utr) ? "border-rose-300 bg-rose-50" : ""}`} />
                                                {form.utr && !isValidUtr(form.utr) && (
                                                    <p data-testid="verify-utr-error" className="mt-1 text-[11px] font-medium text-rose-600">{UTR_FORMAT_ERROR}</p>
                                                )}
                                                {sel?.reference_number && (
                                                    <p data-testid="verify-original-reference" className="mt-1 text-[11px] text-slate-500">
                                                        Original Reference: <span className="font-mono font-semibold text-slate-700">{sel.reference_number}</span>
                                                        <span className="ml-1 text-amber-600">· kept on record</span>
                                                    </p>
                                                )}
                                                {utrUpdatedFlash && (
                                                    <span data-testid="utr-updated-flash" className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                                        <CheckCircle size={12} weight="fill" /> UTR Updated
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <Label className="text-xs">Amount {ocrMismatch("amount") && <span className="ml-1 text-rose-600 font-bold" data-testid="mismatch-amount">⚠ OCR: ₹{Number(ocrResult.amount).toLocaleString("en-IN")}</span>}</Label>
                                                <Input data-testid="verify-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`mt-1 ${ocrMismatch("amount") ? mismatchCls : ""}`} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Payment Date {ocrMismatch("payment_date") && <span className="ml-1 text-rose-600 font-bold" data-testid="mismatch-date">⚠ OCR: {dmY(ocrResult.payment_date)}</span>}</Label>
                                                <Input data-testid="verify-date" type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className={`mt-1 ${ocrMismatch("payment_date") ? mismatchCls : ""}`} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Portal</Label>
                                                <select data-testid="verify-portal" value={form.portal_id || ""} onChange={(e) => setForm({ ...form, portal_id: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                                                    <option value="">— Auto (match by UPI) —</option>
                                                    {portals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <Label className="text-xs flex items-center justify-between">
                                                    <span>Receiver UPI</span>
                                                    {form.receiver_upi_id && companyUpis.some((u) => (u.upi_id || "").toLowerCase() === (form.receiver_upi_id || "").toLowerCase()) && (
                                                        <span className="text-[9px] font-semibold uppercase tracking-wider text-emerald-700">Whitelisted</span>
                                                    )}
                                                </Label>
                                                <div className="mt-1 flex gap-1">
                                                    <Input
                                                        data-testid="verify-upi"
                                                        value={form.receiver_upi_id}
                                                        onChange={(e) => setForm({ ...form, receiver_upi_id: e.target.value })}
                                                        placeholder={sel.receiver_upi_id ? "" : "Not detected — pick from whitelist"}
                                                        className={`font-mono text-xs ${ocrMismatch("receiver_upi_id") ? mismatchCls : ""}`}
                                                        list="upi-whitelist"
                                                    />
                                                    <datalist id="upi-whitelist">
                                                        {companyUpis.filter((u) => u.active !== false).map((u) => (
                                                            <option key={u.id || u.upi_id} value={u.upi_id}>{u.label || u.upi_id}</option>
                                                        ))}
                                                    </datalist>
                                                </div>
                                                {form.receiver_upi_id && (
                                                    <button
                                                        type="button"
                                                        data-testid="verify-reject-upi-btn"
                                                        onClick={() => rejectReceiverUpi(form.receiver_upi_id)}
                                                        disabled={blockingUpi}
                                                        title="Flag this UPI as not belonging to us — future slips paid here will be blocked at upload."
                                                        className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
                                                    >
                                                        <ShieldSlash size={13} weight="fill" /> {blockingUpi ? "Rejecting…" : "Reject UPI"}
                                                    </button>
                                                )}
                                                {companyUpis.length > 0 && (!form.receiver_upi_id || !companyUpis.some((u) => (u.upi_id || "").toLowerCase() === (form.receiver_upi_id || "").toLowerCase())) && (
                                                    <div className="mt-1 flex flex-wrap gap-1" data-testid="verify-upi-suggestions">
                                                        {companyUpis.filter((u) => u.active !== false).slice(0, 6).map((u) => (
                                                            <button
                                                                key={u.id || u.upi_id}
                                                                type="button"
                                                                onClick={() => setForm({ ...form, receiver_upi_id: u.upi_id })}
                                                                className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-700 hover:bg-blue-100 hover:text-blue-800"
                                                                title={u.label || ""}
                                                            >
                                                                {u.upi_id}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Notes</Label>
                                            <Textarea data-testid="verify-notes" rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 min-h-[36px] resize-none" />
                                        </div>
                                        {sel.status !== "pending" && payError && (
                                            <div data-testid="edit-error-inline" role="alert" className="mb-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{payError}</div>
                                        )}
                                        {sel.status !== "pending" && (
                                            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
                                                {isDirty && (
                                                    <button
                                                        type="button"
                                                        data-testid="edit-reset-btn"
                                                        onClick={() => setForm({
                                                            utr: sel.utr || "",
                                                            amount: sel.amount || "",
                                                            payment_date: sel.payment_date || "",
                                                            notes: sel.verification_notes || "",
                                                            receiver_upi_id: sel.receiver_upi_id || "",
                                                        })}
                                                        className="rounded-md px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                                                    >
                                                        Reset
                                                    </button>
                                                )}
                                                <Button
                                                    type="button"
                                                    data-testid="edit-save-btn"
                                                    onClick={saveEdits}
                                                    disabled={!isDirty || editBusy}
                                                    className="h-8 bg-slate-900 text-white hover:bg-slate-800"
                                                >
                                                    {editBusy ? "Saving…" : isDirty ? "Save changes" : "No changes"}
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {sel.status === "pending" && (
                                        <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/95 px-5 py-2.5 backdrop-blur">
                                            {payError && (
                                                <div data-testid="verify-error-inline" role="alert" className="mb-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{payError}</div>
                                            )}
                                            <div className="flex gap-3">
                                                {isDirty ? (
                                                    <>
                                                        <Button
                                                            type="button"
                                                            data-testid="pending-edit-reset-btn"
                                                            onClick={() => setForm({
                                                                utr: sel.utr || "",
                                                                amount: sel.amount || "",
                                                                payment_date: sel.payment_date || "",
                                                                notes: sel.verification_notes || "",
                                                                receiver_upi_id: sel.receiver_upi_id || "",
                                                            })}
                                                            variant="outline"
                                                            className="border-slate-300 text-slate-600 hover:bg-slate-100"
                                                        >
                                                            Reset
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            data-testid="pending-save-btn"
                                                            onClick={() => savePendingEdits(false)}
                                                            disabled={editBusy}
                                                            className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                                                        >
                                                            <CheckCircle size={18} className="mr-2" />
                                                            {editBusy ? "Saving…" : "Save changes"}
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        type="button"
                                                        data-testid="verify-payment-btn"
                                                        onClick={requestVerify}
                                                        disabled={anyMismatch}
                                                        className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        title={anyMismatch ? "OCR shows mismatches — click 'Use OCR values' or edit the fields first" : ""}
                                                    >
                                                        <CheckCircle size={18} className="mr-2" />
                                                        {anyMismatch
                                                            ? "Fix mismatches first"
                                                            : (sel.upi_matched ? "Verify & Record Payment" : "Verify (UPI not whitelisted)")}
                                                    </Button>
                                                )}
                                                <Button type="button" data-testid="reject-payment-btn" onClick={() => act("rejected", { force_upi: true, force_dup: true })} variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50">
                                                    <XCircle size={18} className="mr-2" /> Reject
                                                </Button>
                                                {nextInfo?.has_more && (
                                                    <Button
                                                        type="button"
                                                        data-testid="next-unchecked-btn"
                                                        onClick={gotoNextUnchecked}
                                                        variant="outline"
                                                        className="border-blue-300 text-blue-800 hover:bg-blue-50"
                                                        title={`Skip to the next pending slip · ${nextInfo.remaining - 1} more after this`}
                                                    >
                                                        Next unchecked → <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-800">{nextInfo.remaining - 1}</span>
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {sel.status === "rejected" && (
                                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="rejected-actions-block">
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div>
                                                    <div className="font-display text-sm font-semibold text-slate-900">Rejected slip — actions</div>
                                                    <div className="mt-0.5 text-xs text-slate-600">Accept posts it to the ledger. Restore puts it back into Pending. Delete removes it permanently.</div>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <Button
                                                        data-testid="drawer-rejected-accept"
                                                        onClick={async () => {
                                                            if (!window.confirm(`Accept this rejected slip and post it to ${partyName(sel.party_id)}'s ledger?\n\nUTR: ${sel.utr || "—"}\nAmount: ₹${(sel.amount || 0).toLocaleString("en-IN")}`)) return;
                                                            try {
                                                                await api.post(`/uploads/${sel.id}/accept-rejected`);
                                                                toast.success("Slip accepted — ledger updated");
                                                                setSel(null);
                                                                load();
                                                            } catch (e) { toast.error(formatApiError(e)); }
                                                        }}
                                                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                                                    >
                                                        <CheckCircle size={15} weight="fill" className="mr-1.5" /> Accept
                                                    </Button>
                                                    <Button
                                                        data-testid="drawer-rejected-restore"
                                                        onClick={async () => {
                                                            if (!window.confirm("Move this slip back to Pending for re-verification?")) return;
                                                            try {
                                                                await api.post(`/uploads/${sel.id}/restore`);
                                                                toast.success("Slip restored to pending");
                                                                setSel(null);
                                                                load();
                                                            } catch (e) { toast.error(formatApiError(e)); }
                                                        }}
                                                        variant="outline"
                                                        className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                                    >
                                                        <ArrowClockwise size={15} className="mr-1.5" /> Restore
                                                    </Button>
                                                    <Button
                                                        data-testid="drawer-rejected-delete"
                                                        onClick={async () => {
                                                            if (!window.confirm(`DELETE this slip permanently?\n\nUTR: ${sel.utr || "—"}\nAmount: ₹${(sel.amount || 0).toLocaleString("en-IN")}\n\nThis cannot be undone.`)) return;
                                                            try {
                                                                await api.delete(`/uploads/${sel.id}`);
                                                                toast.success("Slip deleted");
                                                                setSel(null);
                                                                load();
                                                            } catch (e) { toast.error(formatApiError(e)); }
                                                        }}
                                                        variant="outline"
                                                        className="border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                                        style={canDelete ? undefined : { display: "none" }}
                                                    >
                                                        <Trash size={15} className="mr-1.5" /> Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {(sel.status === "verified" || sel.status === "pending" || sel.status === "rejected") && (
                                        <div className={`grid gap-3 ${sel.status === "verified" ? "lg:grid-cols-2" : ""}`}>
                                            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3" data-testid="reassign-party-block">
                                                <div className="flex items-start gap-2">
                                                    <ArrowClockwise size={16} weight="bold" className="mt-0.5 shrink-0 text-blue-700" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-display text-xs font-semibold text-blue-900">
                                                            Wrong party? Reassign this payment
                                                        </div>
                                                        <div className="mt-0.5 text-[11px] text-blue-800/80 truncate" title={partyName(sel.party_id)}>
                                                            Currently posted to <span className="font-semibold">{partyName(sel.party_id)}</span>
                                                        </div>
                                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                                            <div className="min-w-[160px] flex-1">
                                                                <Select value={reassignParty} onValueChange={setReassignParty}>
                                                                    <SelectTrigger data-testid="reassign-party-select" className="h-8 bg-white text-xs"><SelectValue placeholder="Select new party" /></SelectTrigger>
                                                                    <SelectContent>
                                                                        {parties.filter((p) => p.id !== sel.party_id && p.active !== false).map((p) => (
                                                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                            <Button
                                                                data-testid="reassign-party-btn"
                                                                onClick={reassign}
                                                                disabled={!reassignParty || reassignParty === sel.party_id || reassignBusy}
                                                                className="h-8 bg-blue-700 text-xs text-white hover:bg-blue-800"
                                                            >
                                                                {reassignBusy ? "Moving…" : "Move"}
                                                            </Button>
                                                        </div>
                                                        {sel.status === "verified" && (
                                                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-blue-200/60 pt-2">
                                                                <div className="text-[11px] text-blue-800/80">
                                                                    Ledger out of sync after a move? Rebuild the payment / commission entries from this slip.
                                                                </div>
                                                                <Button
                                                                    data-testid="rebuild-ledger-btn"
                                                                    onClick={rebuildLedger}
                                                                    disabled={rebuildBusy}
                                                                    variant="outline"
                                                                    className="h-8 shrink-0 border-blue-300 text-xs text-blue-800 hover:bg-blue-100"
                                                                >
                                                                    <ArrowClockwise size={13} className="mr-1" />
                                                                    {rebuildBusy ? "Rebuilding…" : "Rebuild ledger entries"}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {sel.status === "verified" && (
                                                <div className="rounded-lg border border-rose-200 bg-rose-50/70 p-3">
                                                    <div className="flex items-start gap-2">
                                                        <XCircle size={16} weight="bold" className="mt-0.5 shrink-0 text-rose-700" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-display text-xs font-semibold text-rose-900">Recorded a wrong payment or duplicate?</div>
                                                            <div className="mt-0.5 text-[11px] text-rose-800/80">Rejecting reverses the payment + commission entries for this slip.</div>
                                                            <Button
                                                                data-testid="reverse-payment-btn"
                                                                onClick={() => {
                                                                    if (window.confirm("Reject this verified payment and reverse the ledger entries?")) {
                                                                        act("rejected", { force_upi: true, force_dup: true });
                                                                    }
                                                                }}
                                                                variant="outline"
                                                                className="mt-2 h-8 shrink-0 border-rose-300 text-xs text-rose-700 hover:bg-rose-100"
                                                            >
                                                                <XCircle size={13} className="mr-1" /> Reject & reverse
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {/* Duplicate UTR warning modal */}
            <Dialog open={!!dupWarning} onOpenChange={(v) => !v && setDupWarning(null)}>
                <DialogContent data-testid="duplicate-utr-dialog" className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2 text-rose-700">
                            <Warning size={22} weight="fill" /> Duplicate UTR detected
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-600">
                            {dupWarning?.message}
                        </DialogDescription>
                    </DialogHeader>
                    {dupWarning?.duplicates?.length > 0 && (
                        <div className="space-y-2" data-testid="duplicate-utr-list">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Already recorded</div>
                            {dupWarning.duplicates.map((d) => (
                                <div key={d.id} className="rounded-lg border border-rose-200 bg-rose-50/70 p-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="font-semibold text-slate-900" data-testid="duplicate-party-name">{d.party_name}</div>
                                        <StatusBadge status={d.status} />
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                                        <span>Amount: <span className="font-mono font-semibold text-slate-800">₹ {Number(d.amount || 0).toLocaleString("en-IN")}</span></span>
                                        <span>Date: {d.payment_date || "—"}</span>
                                        {d.verified_at && <span>Verified: {d.verified_at.slice(0, 10)}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        Verifying this slip will record a <span className="font-semibold">second payment for the same UTR</span>. This is usually a mistake — the same transaction cannot settle two parties.
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            data-testid="duplicate-cancel-btn"
                            onClick={() => setDupWarning(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="duplicate-override-btn"
                            onClick={() => dupWarning?.onConfirm?.()}
                            className="bg-rose-600 text-white hover:bg-rose-700"
                        >
                            Verify anyway
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* v1.31.47 — Link-UPI-to-portal prompt shown on verify when the receiver
                UPI isn't yet linked to any portal. Admin can save it or skip. */}
            <Dialog open={!!portalLinkPrompt} onOpenChange={(o) => { if (!o && !linkingPortal) setPortalLinkPrompt(null); }}>
                <DialogContent data-testid="link-upi-portal-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Do you want to save this UPI ID to a portal?</DialogTitle>
                        <DialogDescription>
                            This Receiver UPI isn't linked to any portal yet. Link it so future payments show the correct portal name automatically — or skip and just verify.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Receiver UPI ID</Label>
                            <div data-testid="link-upi-value" className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm font-semibold text-slate-900 break-all">
                                {portalLinkPrompt?.upi}
                            </div>
                        </div>
                        <div>
                            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Portal</Label>
                            <Select value={portalLinkPrompt?.portalId || ""} onValueChange={(v) => setPortalLinkPrompt((p) => ({ ...p, portalId: v }))}>
                                <SelectTrigger data-testid="link-upi-portal-select" className="mt-1"><SelectValue placeholder="Select a portal…" /></SelectTrigger>
                                <SelectContent>
                                    {portals.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button type="button" variant="outline" data-testid="link-upi-skip-btn" onClick={skipPortalLinkAndVerify} disabled={linkingPortal} className="border-slate-300 text-slate-700">
                            Skip &amp; Verify
                        </Button>
                        <Button type="button" data-testid="link-upi-save-btn" onClick={saveUpiToPortalAndVerify} disabled={linkingPortal || !portalLinkPrompt?.portalId} className="bg-emerald-600 text-white hover:bg-emerald-700">
                            {linkingPortal ? "Saving…" : "Save & Verify"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
            <div className="mt-0.5 truncate text-xs text-slate-900" title={typeof value === "string" ? value : undefined}>{value}</div>
        </div>
    );
}

/**
 * Large slip preview inside the drawer + click-to-zoom lightbox with rotate/download.
 * Detects PDFs and embeds them via <object>.
 */
function SlipPreview({ fileUrl }) {
    const [zoom, setZoom] = React.useState(false);
    const [rot, setRot] = React.useState(0);
    const [loaded, setLoaded] = React.useState(false);
    const [err, setErr] = React.useState(false);
    const [attempt, setAttempt] = React.useState(0); // cache-busting attempt counter
    // Inline zoom (mouse-wheel) — 1.0 = fit, up to 5x. Pan when zoomed > 1.
    const [scale, setScale] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const dragRef = React.useRef(null);
    const isPdf = /\.pdf(\?|$)/i.test(fileUrl || "");

    React.useEffect(() => { setLoaded(false); setErr(false); setRot(0); setAttempt(0); setScale(1); setPan({ x: 0, y: 0 }); }, [fileUrl]);

    // Auto-retry once on image load failure — handles transient network blips + PWA cache misses
    const handleImgError = React.useCallback(() => {
        if (attempt < 2) {
            const wait = 400 * (attempt + 1);
            setTimeout(() => setAttempt((a) => a + 1), wait);
        } else {
            setErr(true);
        }
    }, [attempt]);

    // Escape closes zoom
    React.useEffect(() => {
        if (!zoom) return;
        const onKey = (e) => { if (e.key === "Escape") setZoom(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [zoom]);

    // Mouse-wheel zoom on the inline slip (Task 1). preventDefault → page doesn't scroll.
    const onWheel = React.useCallback((e) => {
        if (isPdf || err || !loaded) return;
        e.preventDefault();
        const delta = -e.deltaY;
        setScale((s) => {
            const next = Math.min(5, Math.max(1, s + (delta > 0 ? 0.15 : -0.15)));
            if (next <= 1.01) setPan({ x: 0, y: 0 });
            return Math.round(next * 100) / 100;
        });
    }, [isPdf, err, loaded]);

    // Drag-to-pan when zoomed in
    const onMouseDown = (e) => {
        if (scale <= 1.01 || isPdf) return;
        dragRef.current = { startX: e.clientX - pan.x, startY: e.clientY - pan.y };
    };
    const onMouseMove = (e) => {
        if (!dragRef.current) return;
        setPan({ x: e.clientX - dragRef.current.startX, y: e.clientY - dragRef.current.startY });
    };
    const endDrag = () => { dragRef.current = null; };

    // Add a cache-buster on retry so the browser + service worker don't serve a stale failure
    const displayUrl = fileUrl ? (attempt > 0 ? `${fileUrl}${fileUrl.includes("?") ? "&" : "?"}_r=${attempt}` : fileUrl) : "";

    return (
        <>
            <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-[repeating-conic-gradient(#1f2937_0%_25%,#111827_0%_50%)] bg-[length:22px_22px] p-1">
                {err ? (
                    <div className="text-center text-sm text-slate-300" data-testid="slip-preview-error">
                        <div className="mb-2">Slip file couldn't load after {attempt + 1} attempts.</div>
                        <button
                            type="button"
                            onClick={() => { setErr(false); setLoaded(false); setAttempt((a) => a + 1); }}
                            className="mr-3 rounded bg-yellow-500 px-3 py-1 text-xs font-semibold text-black hover:bg-yellow-400"
                        >
                            Retry
                        </button>
                        <a href={fileUrl} target="_blank" rel="noreferrer" className="text-yellow-400 underline">
                            Open in new tab
                        </a>
                    </div>
                ) : isPdf ? (
                    <object data={displayUrl} type="application/pdf" className="h-full min-h-[420px] w-full rounded" data-testid="slip-preview">
                        <div className="p-6 text-center text-sm text-slate-300">
                            PDF preview isn't supported here.
                            <a href={fileUrl} target="_blank" rel="noreferrer" className="ml-2 text-yellow-400 underline">Open PDF</a>
                        </div>
                    </object>
                ) : (
                    <img
                        src={displayUrl}
                        alt="Payment slip"
                        data-testid="slip-preview"
                        onLoad={() => setLoaded(true)}
                        onError={handleImgError}
                        onClick={() => { if (scale <= 1.01) setZoom(true); }}
                        onWheel={onWheel}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={endDrag}
                        onMouseLeave={endDrag}
                        onDoubleClick={() => { setScale(1); setPan({ x: 0, y: 0 }); }}
                        title="Scroll to zoom · double-click to reset · click to open lightbox"
                        draggable={false}
                        style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale}) rotate(${rot}deg)`,
                            cursor: scale > 1.01 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
                            transformOrigin: "center center",
                        }}
                        className={`max-h-[92vh] h-auto w-full max-w-full select-none rounded object-contain shadow-lg transition-transform duration-100 ${loaded ? "opacity-100" : "opacity-0"}`}
                    />
                )}
                {scale > 1.01 && loaded && !err && !isPdf && (
                    <div className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white shadow-md" data-testid="slip-zoom-level">
                        {Math.round(scale * 100)}% · scroll or dbl-click to reset
                    </div>
                )}
                {!loaded && !err && !isPdf && (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
                        Loading slip{attempt > 0 ? ` (attempt ${attempt + 1})` : ""}…
                    </div>
                )}

                {!err && !isPdf && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
                        <div className="pointer-events-auto flex items-center gap-1 rounded-full bg-black/60 px-3 py-1.5 text-xs text-white backdrop-blur">
                            <button
                                type="button"
                                data-testid="slip-rotate-btn"
                                onClick={(e) => { e.stopPropagation(); setRot((r) => (r + 90) % 360); }}
                                className="rounded p-1 hover:bg-white/10"
                                title="Rotate 90°"
                                aria-label="Rotate"
                            >
                                <ArrowClockwise size={14} />
                            </button>
                            <div className="h-3 w-px bg-white/20" />
                            <button
                                type="button"
                                data-testid="slip-zoom-btn"
                                onClick={(e) => { e.stopPropagation(); setZoom(true); }}
                                className="flex items-center gap-1 rounded px-2 py-0.5 hover:bg-white/10"
                                title="Zoom / full screen"
                            >
                                <MagnifyingGlassPlus size={14} /> <span>Zoom</span>
                            </button>
                            <div className="h-3 w-px bg-white/20" />
                            <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="slip-open-btn"
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 rounded px-2 py-0.5 hover:bg-white/10"
                                title="Open in new tab"
                            >
                                <ArrowSquareOut size={14} /> <span>New tab</span>
                            </a>
                        </div>
                    </div>
                )}
            </div>

            {/* Zoom lightbox */}
            {zoom && !isPdf && (
                <div
                    data-testid="slip-lightbox"
                    onClick={() => setZoom(false)}
                    className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95 p-4"
                >
                    <button
                        type="button"
                        aria-label="Close"
                        data-testid="slip-lightbox-close"
                        onClick={() => setZoom(false)}
                        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                    >
                        <X size={18} />
                    </button>
                    <button
                        type="button"
                        aria-label="Rotate"
                        data-testid="slip-lightbox-rotate-btn"
                        onClick={(e) => { e.stopPropagation(); setRot((r) => (r + 90) % 360); }}
                        className="absolute right-4 top-16 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                    >
                        <ArrowClockwise size={18} />
                    </button>
                    <img
                        src={fileUrl}
                        alt="Payment slip zoomed"
                        onClick={(e) => e.stopPropagation()}
                        style={{ transform: `rotate(${rot}deg)` }}
                        className="max-h-[95vh] max-w-[95vw] cursor-zoom-out rounded shadow-2xl transition-transform duration-300"
                    />
                </div>
            )}
        </>
    );
}

